import type { Translations } from './ru'

export const enTranslations: Translations = {
  langSwitcher: {
    aria: 'Switch language',
    ru: 'Русский',
    en: 'English',
  },

  nav: {
    blog: 'Blog',
  },

  badge: {
    local: 'In browser',
    localTitle: 'The file is processed in your browser and never sent to a server',
  },

  units: {
    mib: 'MiB',
    kib: 'KiB',
    mb: 'MB',
  },

  common: {
    download: 'Download',
    retry: 'Try again',
    errorTitle: 'Error',
    done: 'Done',
    file: 'File',
    size: 'Size',
    sourceSize: 'Source size',
    pickPdf: 'Pick a PDF file',
    pickExcel: 'Pick an Excel spreadsheet',
    pickFile: 'Choose a file',
    deleteFile: 'Remove file',
    dragPdf: 'Drop a PDF here or ',
    dragExcel: 'Drop a XLSX, XLS or ODS here or ',
    clickToPick: 'click to choose',
    pdfLimit: 'PDF only, up to {size} MB',
    sizeLimit: 'Up to {size} MB',
    localBadge: 'The file stays in your browser — nothing is uploaded',
    tooLarge: 'Files over {size} MB are not supported yet.',
  },

  pdfViewer: {
    pickFile: 'Choose a PDF or drop it here',
    limit: 'One PDF up to {size} MB and {pages} pages',
    localNotice: 'The PDF stays on your device: only normal page assets use the network, never the document bytes.',
    opening: 'Opening the PDF locally…',
    rendering: 'Rendering page…',
    previewTitle: 'PDF preview',
    fit: 'Fit',
    zoomOut: 'Zoom out',
    zoomIn: 'Zoom in',
    previous: 'Previous',
    next: 'Next',
    pageOf: 'Page {page} of {pages}',
    pageAlt: 'PDF page {page}',
    reset: 'Close PDF',
    chooseAnother: 'Choose another PDF',
    errorTitle: 'Could not open the PDF',
    fileInfo: 'File information',
    fileName: 'File name',
    fileSize: 'File size',
    pageCount: 'Page count',
    actionsTitle: 'Continue working with this PDF',
    actionsDescription: 'The source file is not transferred automatically. Choose an action and add the PDF on that tool page.',
    errors: {
      empty: 'The file is empty. Choose a non-empty PDF.',
      unsupported: 'Only PDF files are supported.',
      too_large: 'The PDF exceeds the 32 MB limit. Reduce its size and try again.',
      corrupt: 'The PDF is damaged or has an invalid structure. Try another copy of the file.',
      encrypted: 'The PDF is password-protected or encrypted. Remove the protection before opening it again.',
      no_pages: 'This PDF has no pages to display.',
      too_many_pages: 'This PDF has more than 400 pages. Split it into smaller parts before viewing.',
      unsafe_structure: 'The PDF structure exceeds safe local limits. Re-save it or split it into smaller parts.',
      page_too_large: 'This page is too large to render safely in the browser.',
      canvas_unavailable: 'Your browser cannot render the page safely. Try an up-to-date browser.',
      timeout: 'Opening or rendering the page took too long and was stopped.',
      render_failed: 'Could not display this page. Try another page or choose another PDF.',
    },
  },

  hero: {
    title: 'All the PDF tools you need — online and free',
    sub: 'Everything runs in your browser, so the file stays on your device. No sign-up, no watermarks.',
    searchLabel: 'Find a tool',
    searchPlaceholder: 'What do you need? For example, compress or merge',
    noResults: 'Nothing found. Try another word or open the full catalog.',
    clearSearch: 'Show all tools',
    toolsLabel: 'Tools',
    categoryLabels: {
      compression: {
        title: 'PDF compression',
        description: 'Reduce file size right in your browser.',
      },
      'pdf-conversion': {
        title: 'PDF conversion',
        description: 'Turn PDFs into text and create PDFs from HTML or Excel spreadsheets.',
      },
      'pdf-editing': {
        title: 'PDF editing',
        description: 'Rotate, merge, split, crop and clean up PDF documents.',
      },
      'image-pdf': {
        title: 'Images and PDF',
        description: 'Export PDF pages as images or create PDFs from JPG, PNG and HEIC files.',
      },
      security: {
        title: 'Passwords and security',
        description: 'Add a password or remove a known opening password without extra accounts.',
      },
    },
    aboutLabel: 'About',
    aboutTitle: 'PDF tools that run in your browser',
    aboutPara1Prefix:
      'pdfy.ru is a free set of PDF tools. Your file is processed on your device and goes nowhere: ',
    aboutPara1Tool1: 'rotate PDF',
    aboutPara1Mid: ' and ',
    aboutPara1Tool2: 'PDF to JPG',
    aboutPara1Suffix:
      ', JPG to PDF, HEIC to PDF and PDF to Text run via pdf-lib, pdf.js and a HEIC WASM decoder. It is easy to verify: open the Network tab in developer tools and you will see no requests carrying your file.',
    aboutPara3:
      'No sign-up, no watermarks on the result and no paid plans. The tools cover compression, conversion, merge, split, crop, page numbers, watermarks, passwords, and PDF creation from JPG, PNG or HEIC images.',
  },

  tool: {
    crossPromoHeading: 'Related PDF tools',
    crossPromoAllTools: 'All PDF tools',
    faqHeading: 'FAQ',
    howToHeading: 'How it works',
  },

  pdfPreview: {
    title: 'Page preview',
    description: 'Check the document before downloading the result.',
    totalPages: 'Pages: {pages}',
    renderedPages: 'Showing thumbnails: {shown} of {total}.',
    limited: 'For large PDFs we render the first {limit} pages; the rest stay available by page number.',
    failed: 'Could not show thumbnails. You can continue using page numbers.',
    loading: 'Preview…',
    pageLabel: 'Page {page}',
    pageAlt: 'Page {page}',
    selected: 'Selected',
    removed: 'Remove',
    rotated: 'Rotate',
  },

  signPdf: {
    notice: 'This tool adds a visual signature or stamp. It is not a qualified/certificate-based digital signature and is not legal advice.',
    dropPdf: 'Drop a PDF here or choose a file', processedLocally: 'processed locally', chooseAnother: 'Choose another',
    type: 'Type', draw: 'Draw', upload: 'Upload PNG/JPG', signatureText: 'Signature text', signaturePlaceholder: 'Name or signature', create: 'Create',
    useDrawing: 'Use drawing', clear: 'Clear', createdSignature: 'Created signature', instructions: 'Select pages. Drag the signature on the active preview; use the bottom-right handle to resize it.',
    preparingPreview: 'Preparing page previews…', partialPreview: 'Some previews are unavailable, but every page remains selectable by number.', previewError: 'Could not read the PDF or determine its pages. Choose another file.',
    showingPreviews: 'Showing the first {limit} previews of {total}; remaining pages are selectable by number.', pagesToSign: 'Pages to sign', rangesPlaceholder: 'For example: 1-3, 7, 10-12', apply: 'Apply', invalidPages: 'Enter up to {limit} pages from 1 to {total}.',
    selectAll: 'Select all', maxPages: 'You can sign no more than {limit} pages at once.', clearSelection: 'Clear selection', selectedCount: 'Selected: {selected} of {limit}', page: 'Page',
    signaturePosition: 'Signature position on page {page}. Arrow keys move the signature.', resizeSignature: 'Resize signature. Arrow keys change width and height.', noPreview: 'No preview', adding: 'Adding signature…', creating: 'Creating signature…', add: 'Add visual signature', download: 'Download signed PDF',
    choosePdf: 'Choose a PDF file.', fileTooLarge: 'The file is larger than 100 MB.', emptyText: 'Enter signature text.', typedError: 'Could not create the typed signature.', emptyDrawing: 'Draw a signature first.', drawingError: 'Could not save the drawn signature.', invalidImage: 'Choose a PNG or JPG up to 10 MB.', imageTooLarge: 'The image is too large. The maximum is 25 megapixels.', imageError: 'Could not read the signature image. Check the PNG/JPG file.', saveError: 'Could not add the signature. Check the PDF and try again.',
  },

  jpgVPdf: {
    pickImages: 'Pick JPG or PNG images',
    dropImages: 'Drop JPG or PNG here or ',
    limit: 'JPG/PNG, up to {size} MB total',
    selectedCount: 'Selected files: {count}',
    unsupportedFiles: 'Some files were skipped: only JPG, JPEG and PNG are supported.',
    unsupportedBrowser:
      'Your browser does not support WebAssembly or Web Workers, which the local HEIC decoder needs. Try an up-to-date Chrome, Safari, Firefox or Edge.',
    tooLarge: 'Total size is over {size} MB — reduce image size or choose fewer files.',
    conversionError:
      'Could not create the PDF. Check that the images are not corrupted and try again.',
    successDetails: 'PDF is ready, file size: {size}.',
    convert: 'Create PDF',
    processing: 'Creating PDF…',
    downloadPdf: 'Download PDF',
    clear: 'Clear',
  },

  'png-v-pdf': {
    pickImages: 'Pick PNG images',
    dropImages: 'Drop PNG here or ',
    limit: 'PNG/JPG, up to {size} MB total',
    selectedCount: 'Selected files: {count}',
    unsupportedFiles: 'Some files were skipped: only PNG, JPG and JPEG are supported.',
    unsupportedBrowser:
      'Your browser does not support WebAssembly or Web Workers, which the local HEIC decoder needs. Try an up-to-date Chrome, Safari, Firefox or Edge.',
    tooLarge: 'Total size is over {size} MB — reduce image size or choose fewer files.',
    conversionError:
      'Could not create the PDF. Check that the PNG files are not corrupted and try again.',
    successDetails: 'PDF is ready, file size: {size}.',
    convert: 'Create PDF',
    processing: 'Creating PDF…',
    downloadPdf: 'Download PDF',
    clear: 'Clear',
  },

  heicVPdf: {
    pickImages: 'Pick HEIC or HEIF photos',
    dropImages: 'Drop HEIC or HEIF here or ',
    limit: 'HEIC/HEIF, up to {size} MB total',
    selectedCount: 'Selected files: {count}',
    unsupportedFiles: 'Some files were skipped: only HEIC and HEIF are supported.',
    unsupportedBrowser:
      'Your browser does not support WebAssembly or Web Workers, which the local HEIC decoder needs. Try an up-to-date Chrome, Safari, Firefox or Edge.',
    tooLarge: 'Total size is over {size} MB — reduce photo size or choose fewer files.',
    conversionError:
      'Could not read the HEIC/HEIF file and create the PDF. Check that the file is not corrupted and try another photo.',
    successDetails: 'PDF is ready, file size: {size}.',
    convert: 'Create PDF',
    processing: 'Decoding HEIC and creating PDF…',
    downloadPdf: 'Download PDF',
    clear: 'Clear',
  },

  htmlToPdf: {
    pasteMode: 'Paste HTML',
    fileMode: 'HTML file',
    textareaLabel: 'HTML code',
    fileTextareaLabel: 'HTML from file',
    placeholder: '<h1>Heading</h1>\n<p>Document text...</p>',
    inputStats: 'Characters: {chars} · size: {bytes}',
    limit: 'Up to {chars} characters or a {size} MB HTML file',
    localNotice:
      'The tool runs locally: pasted HTML and HTML files are not sent to a server. Page links are not opened.',
    emptyHtml: 'Paste HTML code or upload a .html/.htm file.',
    tooLarge:
      'The HTML is too large. Limit: up to {chars} characters in the field or a {size} MB HTML file.',
    unsupportedFile: 'Only .html and .htm files are supported.',
    unsupportedBrowser:
      'Your browser does not support local PDF generation. Try an up-to-date Chrome, Safari, Firefox or Edge.',
    conversionError:
      'Could not create a PDF from this HTML. Check the markup and try removing external scripts or heavy images.',
    successDetails: 'PDF is ready, file size: {size}.',
    convert: 'Create PDF',
    processing: 'Creating PDF...',
    downloadPdf: 'Download PDF',
    useSample: 'Insert sample',
    clear: 'Clear',
  },

  pdfToText: {
    noOcrNotice:
      'We extract only an existing PDF text layer. Scanned pages and images are not recognised because this tool does not run OCR.',
    submit: 'Extract text',
    loading: 'Extracting text…',
    progressTitle: 'Reading PDF',
    progressPages: 'Page {current} of {total}',
    previewTitle: 'Text preview',
    downloadTxt: 'Download TXT',
    clear: 'Choose another PDF',
    resultStats: 'Pages: {pages} · words: {words} · non-space characters: {chars}',
    tooLittleTextWarning:
      'Very little text was found. This PDF may be made of scans or images — that requires OCR, which this tool does not include yet.',
    noTextLayer:
      'No text layer was found. This is probably a scan or an image-only PDF. This tool does not run OCR, so it cannot recognise that file.',
    encrypted:
      'The PDF is password-protected or encrypted. Remove the protection and try again.',
    invalidPdf:
      'Could not open the PDF. Check that the file is not damaged and is actually a PDF.',
    extractError:
      'Could not extract text from this PDF. Try a different file.',
  },

  comparePdf: {
    leftFile: 'First PDF',
    rightFile: 'Second PDF',
    scopeNotice:
      'The tool compares the PDF text layer page by page and does not run OCR. Scanned documents, image-only PDFs and documents over {pages} pages get a clear message instead of an empty result.',
    submit: 'Compare PDFs',
    loading: 'Comparing PDFs…',
    progressLeft: 'Reading the first PDF',
    progressRight: 'Reading the second PDF',
    progressCompare: 'Comparing text',
    progressPages: 'Page {current} of {total}',
    identicalTitle: 'The text layer matches',
    differentTitle: 'Text differences found',
    resultFiles: '{left} ↔ {right}',
    equalPages: 'Matching pages',
    changedPages: 'Changed',
    addedPages: 'Added',
    removedPages: 'Removed',
    pageCount: 'Pages',
    wordCount: 'Words',
    similarity: 'Page match',
    lowTextWarning:
      'Very little text was found in one of the PDFs. If it is a scan or an image-only file, full comparison requires OCR and may be incomplete.',
    diffHeading: 'Page differences',
    pageChanged: 'Page {page}: text changed',
    pageAdded: 'Page {page}: added in the second PDF',
    pageRemoved: 'Page {page}: removed from the second PDF',
    diffStats: 'Removed lines: {removed} · added lines: {added}',
    removedText: 'Before',
    addedText: 'After',
    noRemovedText: 'No removed lines.',
    noAddedText: 'No added lines.',
    truncatedDiff: 'Only a sample is shown. This page has more changed lines.',
    hiddenDiffs: 'More pages with differences: {count}.',
    clear: 'Compare other PDFs',
    tooManyPages:
      'One of the PDFs has more than {pages} pages. Choose a smaller document for stable local processing.',
    noTextLayer:
      'No text layer was found in at least one PDF. It is probably a scan or an image-only PDF. This tool does not run OCR.',
    encrypted:
      'One of the PDFs is password-protected or encrypted. Remove the protection and try again.',
    invalidPdf:
      'Could not open one of the PDFs. Check that the files are not damaged and are actually PDFs.',
    compareError:
      'Could not compare these PDFs. Try different files or reduce the document size.',
  },

  extractPdfImages: {
    scopeTitle: 'What is extracted',
    scopeBody:
      'We look for embedded raster images inside the PDF through pdf.js. This is not full-page export: text, vector artwork, complex masks and the original image compression may not be preserved.',
    submit: 'Extract images',
    loading: 'Finding images…',
    progressTitle: 'Reading PDF',
    progressPages: 'Page {current} of {total}',
    noImagesTitle: 'No images found',
    noImagesBody:
      'Checked pages: {pages}. This PDF may not contain separate embedded raster images: pages can be text, vector artwork or already flattened scans.',
    resultStats: 'Images: {images} · pages checked: {pages} · total size: {size}',
    imageMeta: 'Page {page} · {width}×{height}px · {size}',
    previewAlt: 'Extracted image {index}',
    downloadOne: 'Download PNG',
    downloadZip: 'Download ZIP',
    clear: 'Choose another PDF',
    truncated:
      'Only the first found images are shown. The PDF has too many image objects, so some small images may be skipped.',
    encrypted:
      'The PDF is password-protected or encrypted. Remove the protection and try again.',
    browserMemory:
      'The browser ran out of memory while extracting images. Try a smaller file or close extra tabs.',
    extractError:
      'Could not extract images from this PDF. The file may be damaged or use an unsupported image structure.',
  },

  flattenPdf: {
    scopeTitle: 'What the tool supports',
    scopeBody:
      'The confirmed local scenario is flattening AcroForm fields. Generic comments, highlights, stamps, freehand markups and XFA-only forms may stay unchanged.',
    submit: 'Flatten PDF',
    loading: 'Flattening fields…',
    downloadPdf: 'Download flattened PDF',
    again: 'Process another PDF',
    totalPages: 'Pages',
    fieldsFlattened: 'Fields found',
    noFieldsNotice:
      'No standard AcroForm fields were found. The PDF was saved again, but there may be no visible change.',
    xfaNotice:
      'This PDF contains XFA data. The tool locks only standard AcroForm fields; XFA-only behaviour may differ between PDF viewers.',
    encrypted: 'The PDF is password-protected — remove the password and try again.',
    parseError: 'Could not flatten this PDF. The file may be damaged or use unsupported fields.',
  },

  fillPdfForm: {
    pick: 'Choose a PDF with an interactive form',
    limit: 'PDF up to 32 MB, 200 pages and 500 fields',
    local: 'Processed locally in your browser',
    scopeTitle: 'Existing form fields only',
    scope:
      'Supports AcroForm text, checkboxes, radio buttons, dropdowns and single/multiple option lists. Text appearances cover Latin, Cyrillic and common symbols; missing font glyphs are rejected.',
    inspecting: 'Checking the structure and finding fields…',
    summary: 'Pages: {pages} · fields: {fields}',
    changeFile: 'Different PDF',
    flatten: 'Flatten fields after filling',
    flattenHint: 'Values stay visible, but the fields can no longer be edited.',
    download: 'Fill and download PDF',
    saving: 'Preparing the copy…',
    again: 'Choose another PDF',
    types: {
      text: 'Text field',
      checkbox: 'Checkbox',
      radio: 'Radio button',
      dropdown: 'Dropdown',
      'option-list': 'Option list',
    },
    errors: {
      'invalid-pdf': 'The file is damaged, has an invalid format or is not a PDF.',
      encrypted: 'The PDF is password-protected. Remove the protection first.',
      xfa: 'This is an XFA form. The tool supports standard AcroForm fields only.',
      'no-fields':
        'No interactive fields were found. Scans and ordinary PDF text cannot be filled with this tool.',
      'unsupported-field': 'The form contains an unsupported field type.',
      'unsupported-glyph':
        'A value or option uses a character missing from the bundled font. Use Latin, Cyrillic and common symbols.',
      limit: 'The PDF exceeds safe limits for size, pages, objects, fields or options.',
      processing: 'Could not save the filled copy. The source file was not sent to a server.',
    },
  },

  pdfMetadata: {
    scopeBody:
      'You can change the title, author, subject, keywords and dates. Custom fields and the XMP packet stay in the file untouched.',
    titleLabel: 'Title',
    authorLabel: 'Author',
    subjectLabel: 'Subject',
    keywordsLabel: 'Keywords',
    creatorLabel: 'Created in',
    producerLabel: 'PDF written by',
    summaryTitle: 'Pages in the file: {pages}',
    summaryEmpty: 'No title or author is set, and there is no application info.',
    keywordsPlaceholder: 'contract, invoice, archive',
    creationDateLabel: 'Creation date',
    modificationDateLabel: 'Modification date',
    emptyPlaceholder: 'Empty',
    unsupportedTitle: 'Unsupported metadata',
    unsupportedBody:
      'This PDF has metadata outside the supported field set. We keep it in the file but do not edit it through this form.',
    unsupportedResultNotice:
      'The file contained unsupported metadata. It was preserved as-is but not synchronised with form changes.',
    loading: 'Reading metadata…',
    saving: 'Saving PDF…',
    save: 'Save PDF',
    downloadPdf: 'Download PDF with metadata',
    again: 'Process another PDF',
    totalPages: 'Pages',
    encrypted: 'The PDF is password-protected — remove the password and try again.',
    invalidDate: 'One of the dates has an invalid format. Clear the field or pick the date again.',
    parseError:
      'Could not read or save PDF metadata. The file may be damaged or use an unsupported structure.',
  },

  unlockPdf: {
    knownPasswordNotice:
      'This tool works only when you know the opening password. We do not crack PDFs or recover forgotten passwords.',
    passwordLabel: 'Known PDF opening password',
    passwordPlaceholder: 'Enter the password',
    submit: 'Remove password',
    loading: 'Removing password…',
    downloadPdf: 'Download PDF without password',
    clear: 'Pick another PDF',
    successDetails: 'PDF is ready, file size: {size}.',
    resultNotice:
      'Check the downloaded file in a regular PDF viewer: it should open without asking for a password.',
    wrongPassword: 'The password did not work. Check keyboard layout, letter case and try again.',
    unsupportedEncryption:
      'This PDF encryption type is not supported by browser-local processing yet. We do not upload the file to a server.',
    invalidPdf: 'Could not open this PDF. Check that the file is not damaged and is really a PDF.',
    isolationRequired:
      'The browser did not allow the secure WebAssembly mode needed for local decryption. Update the browser or open the page directly.',
    decryptError: 'Could not remove the password from this PDF. Check the file and password, then try again.',
  },

  repairPdf: {
    scopeTitle: 'What the tool can actually repair',
    scopeBody:
      'We run QPDF/WASM locally and rewrite the PDF structure: cross-reference table/stream, trailer and service objects that QPDF can read through its recovery heuristics. This does not recover truncated pages, damaged images, unknown passwords or lost content.',
    submit: 'Repair PDF',
    loading: 'Checking and rewriting PDF…',
    downloadPdf: 'Download repaired PDF',
    clear: 'Pick another PDF',
    successDetails: 'File rewritten locally: before {before}, after {after}.',
    resultNotice:
      'Open the downloaded file in a regular PDF viewer. If pages still fail, the damage is deeper than this structural repair tool supports.',
    encrypted: 'The PDF is password-protected or encrypted. Remove protection first, then try repair.',
    invalidPdf:
      'Could not recognise this file as a PDF. Check the extension and try the original document before truncation or re-upload.',
    unrecoverable:
      'QPDF could not recover this PDF. Common causes are truncated files, missing required objects or damaged page stream data.',
    browserRuntime:
      'The browser could not run local QPDF/WASM. Update the browser, open the page directly or try a smaller file.',
    repairError:
      'Could not repair this PDF. The damage or structure may be outside the browser tool scope.',
  },


  umbrella: {
    h1: 'PDF Converter Online — Free, No Sign-up',
    sub: 'Convert Excel spreadsheets, HTML and images to PDF, and turn PDFs into JPG, PNG or text. Everything runs in your browser, with no sign-up or watermarks.',
    breadcrumb: 'PDF converter',
    schemaName: 'pdfy.ru — PDF converter',
    toolsHeading: 'All tools',
    longFormHeading: 'How to choose the right format',
    para1: 'PDF is the universal format for sharing and printing: it preserves layout on any device and prevents accidental edits. Here you can build a PDF from a spreadsheet, HTML or photos, and pull images or text out of a PDF.',
    para2: 'For spreadsheets, pick Excel to PDF: sheets become pages, text and numbers are carried over without colors or merged cells. We do not convert Word documents or PowerPoint slides: save them as PDF in the editor, then compress the file if needed.',
    para3: 'For tasks without conversion: PDF compression shrinks file size for email; page rotation fixes scan orientation; PDF to JPG lets you embed a page as an image in a slide or document. All tools run entirely in your browser — nothing is uploaded.',
    faqHeading: 'Frequently asked questions',
    faq: [
      {
        q: 'Is it free?',
        a: 'Yes, all tools are completely free. No subscriptions, trial periods or hidden fees.',
      },
      {
        q: 'Do I need to sign up?',
        a: 'No. No accounts, email addresses or passwords — just pick a file and get the result.',
      },
      {
        q: 'Is it safe to pick files here?',
        a: 'Yes. All tools run entirely in your browser: the file never leaves your device and is not stored anywhere.',
      },
      {
        q: 'Which formats are supported?',
        a: 'Input: DOCX, DOC, ODT, RTF; XLSX, XLS, ODS; PPTX, PPT, ODP; PDF. Output: PDF, DOCX, XLSX, JPG.',
      },
      {
        q: 'Is there a file size limit?',
        a: 'Most tools accept files up to 50 MB. This covers the vast majority of typical office documents.',
      },
      {
        q: 'Will there be watermarks on the output?',
        a: 'No. No watermarks, logos or "processed by pdfy.ru" inserts.',
      },
    ],
  },

  utilities: {
    'pdf-viewer': {
      title: 'Open PDF online',
      shortDescription: 'View PDF pages locally in your browser without uploading the file.',
      longDescription: 'Open a PDF right in your browser, move between pages, change zoom and inspect basic document information.',
      marketing: [
        'A quick local preview helps you check a document before printing, sending or processing it without installing a separate application.',
        'The PDF bytes are passed only to pdf.js in your browser and are never uploaded to pdfy. Continue to rotate, split, compress, extract text or convert pages when needed.',
      ],
      faq: [
        { q: 'Is the PDF sent to a server?', a: 'No. The file is read and pages are rendered locally through pdf.js. DevTools → Network will show no request carrying the PDF bytes.' },
        { q: 'Can I open a password-protected PDF?', a: 'No. Encrypted and password-protected PDFs are rejected. Remove the protection first if you know the password.' },
        { q: 'Can I edit or annotate the PDF?', a: 'No. This page is for viewing only. Use the related rotate, split, compress and other PDF tools for specific actions.' },
        { q: 'What are the viewing limits?', a: 'One PDF up to 32 MB and 400 pages. Structurally complex files and oversized pages are rejected before unsafe memory allocation.' },
        { q: 'Do scanned PDFs work?', a: 'Yes, when their pages render correctly. OCR is not performed, so text in a scan does not become selectable.' },
      ],
      howTo: [
        { name: 'Choose a PDF', text: 'Click the upload area or drop one PDF file onto it.' },
        { name: 'Wait for it to open', text: 'pdf.js checks the document locally and finds its page count.' },
        { name: 'Browse the pages', text: 'Use previous and next, fit mode and zoom controls.' },
        { name: 'Choose the next action', text: 'Continue to rotate, split, compress or convert the PDF if needed.' },
      ],
    },
    'document-scanner': {
      title: 'Document scanner online',
      shortDescription: 'Crop and enhance page photos, then download one PDF.',
      longDescription: 'Turn JPG, PNG and WebP document photos into a clean multi-page PDF. Crop, rotate and enhance every page before export.',
      marketing: [
        'This is more than plain JPG to PDF: crop each photo, rotate it, and choose color, grayscale or black-and-white output before export.',
        'Files are processed sequentially in a background browser thread and are never uploaded to a server.',
      ],
      faq: [
        { q: 'Are photos uploaded to a server?', a: 'No. Decoding, cropping, filters and PDF assembly all run locally in your browser.' },
        { q: 'How is this different from JPG to PDF?', a: 'JPG to PDF combines the original images. The scanner first lets you crop, rotate and enhance every page.' },
        { q: 'Will the PDF contain searchable text?', a: 'No. Text is not recognized: the result is made of images.' },
        { q: 'Is this a certified scan?', a: 'No. The tool cleans photos and assembles a PDF; it does not certify authenticity or compliance with official requirements.' },
      ],
      howTo: [
        { name: 'Add page photos', text: 'Choose multiple JPG, PNG or WebP files, or capture a page with your camera.' },
        { name: 'Prepare each page', text: 'Crop the edges, rotate the photo, and adjust color, brightness and contrast.' },
        { name: 'Set the order', text: 'Move pages up or down; the PDF will use the same order.' },
        { name: 'Download the PDF', text: 'Select “Build scanned PDF”. Processing stays local and runs one page at a time.' },
      ],
    },
    'sign-pdf': {
      title: 'Sign PDF',
      shortDescription: 'Place a visual signature on selected PDF pages locally.',
      longDescription: 'Type, draw, or upload a PNG/JPG signature, then place and resize it on PDF pages. This is a visual stamp, not a qualified or certificate-based digital signature.',
      marketing: ['Your PDF is processed locally in the browser and is never uploaded.', 'Place one created signature on one or more selected pages.'],
      faq: [
        { q: 'Is this a certificate-based digital signature?', a: 'No. The tool only adds a visible signature image. It does not create a certificate, identity proof, qualified signature, or audit trail.' },
        { q: 'Is the PDF uploaded?', a: 'No. Preview and signature placement happen locally in your browser.' },
        { q: 'How can I create a signature?', a: 'Type it, draw with a mouse or finger, or upload a PNG/JPG. PNG transparency is preserved.' },
        { q: 'Can I sign multiple pages?', a: 'Yes. Select the pages, adjust position and size, then download the result.' },
      ],
      howTo: [
        { name: 'Upload a PDF', text: 'Choose one PDF file; it remains in your browser.' },
        { name: 'Create a signature', text: 'Type, draw, or upload your signature image.' },
        { name: 'Select pages', text: 'Check every page that should receive the signature.' },
        { name: 'Place the signature', text: 'Drag and resize it on the page preview.' },
        { name: 'Download the PDF', text: 'Apply the visual signature and download the finished file.' },
      ],
    },
    szhat: {
      title: 'Compress PDF to the size you need',
      shortDescription: 'Compress locally or select the 5 MB target and check the result.',
      longDescription:
        'Compresses PDFs locally and checks the result against an optional 5 MB target. The file stays in your browser.',
      marketing: [
        'Shrink a PDF with the quality balance you select — useful for email attachments, portal uploads or device storage.',
        'Processing happens locally in your browser — your file is not uploaded. Images inside the PDF get compressed: JPEG and regular raster pictures. Text and vector graphics stay as they are.',
        'If you need a file no larger than 5 MB, select the target mode. It is a best-effort target, not a guarantee: the result clearly says whether the size was reached.',
      ],
      faq: [
        {
          q: 'Is the file sent to a server?',
          a: 'No. The PDF is compressed right in your browser and never leaves it.',
        },
        {
          q: 'What is the maximum file size?',
          a: 'Up to 100 MB. Most office PDFs are well under this threshold.',
        },
        {
          q: 'What is the quality after compression?',
          a: 'You pick the level: minimal (virtually lossless), standard (balance of size and quality) or strong (noticeable shrink, lower quality). Standard works well for most documents.',
        },
        {
          q: 'Is registration required?',
          a: 'No. The tool is completely free and does not require sign-up.',
        },
        {
          q: 'What if the file did not shrink or got bigger?',
          a: 'If the PDF contains no raster images (for example, plain text only), there is little to compress. In that case the tool tells you so and offers to download as-is.',
        },
        {
          q: 'Does it work for password-protected PDFs?',
          a: 'No. PDFs with a password cannot be processed without removing protection. Remove the password first, then compress.',
        },
        {
          q: 'How can I verify the file does not leave my browser?',
          a: 'Open DevTools (F12) → the Network tab, upload a file and click "Compress". For local processing you will see no network requests carrying your file.',
        },
        {
          q: 'Can any PDF be guaranteed to fit under 5 MB?',
          a: 'No. The final size depends on the document structure: raster images usually compress well, while text-only, vector or already optimised PDFs may barely shrink. The tool reports the actual result without a guarantee.',
        },
        {
          q: 'How will I know the file fits under 5 MB?',
          a: 'Select the 5 MB target. After processing, the page shows the original size, output size and a separate status saying whether the selected target was reached.',
        },
        {
          q: 'What should I do if the file is still over 5 MB?',
          a: 'Try strong compression. If that is still not enough, reduce source images, remove extra pages or export the document again with lower image quality.',
        },
      ],
      howTo: [
        {
          name: 'Pick a file',
          text: 'Click the upload area or drag a PDF file straight into the browser.',
        },
        {
          name: 'Pick a compression level',
          text: 'Choose regular compression or the 5 MB target, then pick "Standard", "Strong", or "Minimal" quality.',
        },
        {
          name: 'Click "Compress PDF"',
          text: 'The file is processed in your browser — no upload happens.',
        },
        {
          name: 'Download the result',
          text: 'Once compression finishes, you see the original and new size and, in target mode, whether the 5 MB target was reached. Download the result in one click.',
        },
      ],
    },
    vJpg: {
      title: 'PDF to JPG',
      shortDescription:
        'Each PDF page becomes a separate JPG image. Right in your browser.',
      longDescription:
        'We render PDFs to JPG directly in your browser via pdf.js. Pick DPI (72/150/300); multi-page documents are packed into a ZIP. Your file never leaves your browser.',
      marketing: [
        'Extract PDF pages as JPG images at the resolution you need. Handy for posting to social media, embedding in slides or sharing document screenshots.',
        'The file is processed locally, in your browser — nothing is uploaded to a server. Multi-page documents are bundled into a ZIP automatically.',
      ],
      faq: [
        {
          q: 'Is the file sent to a server?',
          a: 'No. The PDF is rendered right in your browser via pdf.js. You can verify it in DevTools → Network: there will be no requests carrying your file.',
        },
        {
          q: 'What is the image quality?',
          a: 'You can pick the DPI: 72 (web and social), 150 (versatile) or 300 (print quality). Higher DPI gives a crisper image but increases file size and processing time.',
        },
        {
          q: 'What happens with a multi-page PDF?',
          a: 'Each page becomes a separate JPG file. If there is more than one page, all JPGs are automatically bundled into a ZIP archive for easy download.',
        },
        {
          q: 'What is the maximum file size?',
          a: 'Up to 100 MB. For large documents at high DPI we recommend 72 or 150 DPI so the browser does not run out of memory.',
        },
        {
          q: 'Is registration required?',
          a: 'No, the tool is free and does not require sign-up.',
        },
        {
          q: 'Are password-protected PDFs supported?',
          a: 'No. Remove the password from the PDF first, then convert.',
        },
        {
          q: 'What is the extension of the downloaded files?',
          a: 'For a single-page PDF — one .jpg file. For multi-page — a .zip archive with every page as a JPG.',
        },
      ],
      howTo: [
        {
          name: 'Upload the PDF',
          text: 'Click the upload area or drag a PDF file in.',
        },
        {
          name: 'Pick the quality (DPI)',
          text: '72 DPI suits the web, 150 is a versatile pick, 300 is for print.',
        },
        {
          name: 'Click "Convert to JPG"',
          text: 'The browser renders each PDF page to JPG locally, no server upload.',
        },
        {
          name: 'Download the images',
          text: 'For one page — a single JPG. For several pages — a ZIP archive with a JPG per page.',
        },
      ],
    },
    'pdf-to-png': {
      title: 'PDF to PNG',
      shortDescription:
        'Each PDF page becomes a separate PNG image. Right in your browser.',
      longDescription:
        'We render PDFs to PNG directly in your browser via pdf.js. Pick the DPI resolution; multi-page documents are packed into a ZIP. Your file never leaves your browser.',
      marketing: [
        'Export PDF pages as PNG when you need an image format without JPEG compression: UI screenshots, diagrams, documents with fine text or workflows where crisp lines matter.',
        'The PDF is processed locally in your browser via pdf.js. A single page downloads as PNG, while multiple pages are bundled into a ZIP archive automatically.',
      ],
      faq: [
        {
          q: 'Is the file sent to a server?',
          a: 'No. The PDF is rendered right in your browser via pdf.js. You can verify it in DevTools → Network: there will be no requests carrying your PDF.',
        },
        {
          q: 'How is PNG different from JPG?',
          a: 'PNG keeps the image without JPEG compression and is better for text, diagrams, screenshots and sharp lines. PNG files are usually larger than JPG files.',
        },
        {
          q: 'Is there a quality setting?',
          a: 'There is no separate JPEG-quality control because PNG does not use that kind of lossy compression. You can pick the DPI: 72, 150 or 300 — this controls output resolution.',
        },
        {
          q: 'What happens with a multi-page PDF?',
          a: 'Each page becomes a separate PNG file. If there is more than one page, all PNGs are automatically bundled into a ZIP archive.',
        },
        {
          q: 'What is the maximum file size?',
          a: 'Up to 100 MB. For large documents we recommend 72 or 150 DPI so the browser does not run out of memory.',
        },
        {
          q: 'Are password-protected PDFs supported?',
          a: 'No. Remove the password from the PDF first, then convert.',
        },
      ],
      howTo: [
        {
          name: 'Upload the PDF',
          text: 'Click the upload area or drag a PDF file in.',
        },
        {
          name: 'Pick the resolution',
          text: '72 DPI is good for quick previews, 150 fits most tasks, and 300 is for larger output.',
        },
        {
          name: 'Click "Convert to PNG"',
          text: 'The browser renders each PDF page to PNG locally, without uploading the file to a server.',
        },
        {
          name: 'Download the PNG files',
          text: 'For one page you get one PNG. For several pages you get a ZIP archive with one PNG per page.',
        },
      ],
    },
    jpgVPdf: {
      title: 'JPG to PDF',
      shortDescription: 'Combine JPG and PNG images into one PDF right in your browser.',
      longDescription:
        'We combine JPG and PNG files into a PDF locally in your browser via pdf-lib. Add multiple images, reorder them and download the finished PDF without uploading files to a server.',
      marketing: [
        'Turn photos, scans or images into one PDF for email, portal uploads or printing. Each image becomes a separate PDF page.',
        'Files are processed locally in your browser. Images are not sent to a server and the output has no watermarks.',
      ],
      faq: [
        {
          q: 'Are files sent to a server?',
          a: 'No. JPG and PNG files are assembled into a PDF directly in your browser via pdf-lib. You can verify it in DevTools → Network: there will be no requests carrying your images.',
        },
        {
          q: 'Which formats are supported?',
          a: 'JPG, JPEG and PNG. HEIC, WebP, TIFF and OCR are outside the first release and can be added separately later.',
        },
        {
          q: 'Can I change the page order?',
          a: 'Yes. After adding files, use the up and down arrows — the list order becomes the PDF page order.',
        },
        {
          q: 'What is the maximum file size?',
          a: 'Up to 50 MB total. This protects the browser from running out of memory with very large images.',
        },
        {
          q: 'Will there be a watermark?',
          a: 'No. The PDF is created without logos, captions or watermarks.',
        },
      ],
      howTo: [
        {
          name: 'Add images',
          text: 'Drop JPG or PNG files into the upload area or choose them with the button.',
        },
        {
          name: 'Check the order',
          text: 'Use the up and down arrows to place images in the desired order.',
        },
        {
          name: 'Click "Create PDF"',
          text: 'The browser creates the PDF locally without sending images to a server.',
        },
        {
          name: 'Download the PDF',
          text: 'The finished document has one page per image in the selected order.',
        },
      ],
    },
    'png-v-pdf': {
      title: 'PNG to PDF',
      shortDescription: 'Combine PNG images into one PDF right in your browser.',
      longDescription:
        'We combine PNG files into a PDF locally in your browser via pdf-lib. Add multiple images, reorder them and download the finished PDF without uploading files to a server. JPG is supported as a secondary format.',
      marketing: [
        'Turn PNG screenshots, transparent images or scanned pages into one PDF for sending, printing or uploading. Each image becomes a separate PDF page.',
        'Files are processed locally in your browser. PNG and JPG files are not sent to a server and the output has no watermarks.',
      ],
      faq: [
        {
          q: 'Are files sent to a server?',
          a: 'No. PNG and JPG files are assembled into a PDF directly in your browser via pdf-lib. You can verify it in DevTools → Network: there will be no requests carrying your images.',
        },
        {
          q: 'Which formats are supported?',
          a: 'The primary format for this page is PNG. You can also add JPG and JPEG files when you need a mixed PDF.',
        },
        {
          q: 'Is PNG transparency preserved?',
          a: 'PNG is embedded as an image on the PDF page. The visual result is preserved, but the output PDF is meant for viewing and printing rather than layer editing.',
        },
        {
          q: 'Can I change the page order?',
          a: 'Yes. After adding files, use the up and down arrows — the list order becomes the PDF page order.',
        },
        {
          q: 'What is the maximum file size?',
          a: 'Up to 50 MB total. This protects the browser from running out of memory with very large images.',
        },
      ],
      howTo: [
        {
          name: 'Add PNG files',
          text: 'Drop PNG files into the upload area or choose them with the button. Add JPG files too if needed.',
        },
        {
          name: 'Check the order',
          text: 'Use the up and down arrows to place images in the desired order.',
        },
        {
          name: 'Click "Create PDF"',
          text: 'The browser creates the PDF locally without sending images to a server.',
        },
        {
          name: 'Download the PDF',
          text: 'The finished document has one page per image in the selected order.',
        },
      ],
    },
    heicVPdf: {
      title: 'HEIC to PDF',
      shortDescription: 'Convert HEIC and HEIF photos to PDF locally in your browser.',
      longDescription:
        'HEIC/HEIF photos are decoded in the browser with the heic2any WASM library, then assembled into a PDF with pdf-lib. Files are not uploaded to a server.',
      marketing: [
        'Use it for photos from iPhone and other devices that save images as HEIC or HEIF. Each photo becomes a separate PDF page.',
        'The HEIC decoder loads only on this page and only during conversion. The JPG/PNG tool stays lightweight, while HEIC files are processed locally without watermarks.',
      ],
      faq: [
        {
          q: 'Are files sent to a server?',
          a: 'No. HEIC/HEIF files are decoded in the browser through WebAssembly, then the PDF is created locally with pdf-lib. Files are not uploaded to a server.',
        },
        {
          q: 'Which browsers are supported?',
          a: 'A modern browser with WebAssembly and Web Workers is required: current Chrome, Safari, Firefox or Edge. Native HEIC preview is not universal, so the tool uses a separate local decoder.',
        },
        {
          q: 'Why is this separate from JPG to PDF?',
          a: 'HEIC needs a separate decoder and different compatibility errors. A separate page is clearer for SEO and keeps the regular JPG/PNG tool lightweight.',
        },
        {
          q: 'Are EXIF and location metadata preserved?',
          a: 'No. The PDF uses only the image content; HEIC metadata is not copied into the result.',
        },
        {
          q: 'What is the maximum file size?',
          a: 'Up to 50 MB total. HEIC decoding uses browser memory, so very large photo batches should be converted in smaller groups.',
        },
      ],
      howTo: [
        {
          name: 'Add HEIC or HEIF files',
          text: 'Drop photos into the upload area or choose files with the button.',
        },
        {
          name: 'Check the order',
          text: 'Use the up and down arrows to place photos in the desired order.',
        },
        {
          name: 'Click "Create PDF"',
          text: 'The browser decodes HEIC/HEIF to JPEG locally and creates the PDF without sending files to a server.',
        },
        {
          name: 'Download the PDF',
          text: 'The finished document has one page per photo in the selected order.',
        },
      ],
    },
    htmlToPdf: {
      title: 'HTML to PDF',
      shortDescription:
        'Turn pasted HTML or an HTML file into a PDF locally in your browser.',
      longDescription:
        'We render HTML to PDF right in the browser on your device. The tool accepts pasted code and .html/.htm files; it does not open page links.',
      marketing: [
        'Create a PDF from existing HTML markup: emails, simple reports, instructions, proposals and similar documents can be saved as PDF without installing an editor.',
        'Processing happens locally in your browser. HTML is not sent to a server; external images and styles may still be requested by the browser if your markup references public URLs.',
        'The tool does not open pages by link: that needs a server, and everything here runs in the browser.',
      ],
      faq: [
        {
          q: 'Can I paste a page URL?',
          a: 'No. The first release supports only pasted HTML and HTML files. Public URL capture needs a server browser, queue, timeouts and separate privacy communication.',
        },
        {
          q: 'Is the HTML sent to a server?',
          a: 'No. The PDF is created locally in the browser. If the HTML contains external images, fonts or CSS URLs, the browser may request those resources from their hosts.',
        },
        {
          q: 'Is CSS supported?',
          a: 'Yes, regular page styles, inline style attributes and embedded style tags are applied. Complex interactivity and scripts are not the goal of this tool.',
        },
        {
          q: 'What is the maximum HTML size?',
          a: 'Up to 250,000 characters in the field or a 5 MB HTML file. This protects the browser from freezing during rendering.',
        },
        {
          q: 'Will there be a watermark?',
          a: 'No. The PDF is created without logos, captions or watermarks.',
        },
      ],
      howTo: [
        {
          name: 'Paste HTML or upload a file',
          text: 'Paste markup into the field or choose a .html/.htm file through the HTML file switch.',
        },
        {
          name: 'Check the content',
          text: 'Adjust text, headings and inline styles in the field before converting if needed.',
        },
        {
          name: 'Click "Create PDF"',
          text: 'The browser locally renders the HTML into an A4 page and creates the PDF without sending HTML to a server.',
        },
        {
          name: 'Download the PDF',
          text: 'When generation finishes, download the finished PDF in one click.',
        },
      ],
    },
    pdfToText: {
      title: 'PDF to Text',
      shortDescription:
        'Extract the text layer from a PDF into TXT locally in your browser — no OCR.',
      longDescription:
        'We read the existing PDF text layer with pdf.js right in your browser. Preview the result and download it as .txt. Scanned pages without OCR are not recognised.',
      marketing: [
        'Get plain TXT from a PDF that already contains a text layer — useful for copying, search, rough processing and moving text into an editor.',
        'The file is processed locally in your browser and is not uploaded to a server. The tool is honest about OCR: if the document is made of scans or images, you get a clear explanation instead of an empty “success”.',
      ],
      faq: [
        {
          q: 'Is the file sent to a server?',
          a: 'No. The PDF is read right in your browser through pdf.js and is not uploaded. You can verify this in DevTools → Network.',
        },
        {
          q: 'Does it work with scans?',
          a: 'No. The tool extracts only an existing text layer. Scans and images are not recognized.',
        },
        {
          q: 'Why can the result be empty?',
          a: 'If a PDF looks like text but is actually an image of a page, it has no text layer. In that case the tool explains that the file is likely a scan or image.',
        },
        {
          q: 'Are tables and formatting preserved?',
          a: 'No. The output is plain .txt. Line order depends on the PDF structure and pdf.js capabilities; complex layouts, columns and tables may not transfer perfectly.',
        },
        {
          q: 'Are password-protected PDFs supported?',
          a: 'No. Remove the password or encryption first, then extract text.',
        },
      ],
      howTo: [
        {
          name: 'Upload the PDF',
          text: 'Choose a PDF file or drag it into the upload area.',
        },
        {
          name: 'Click “Extract text”',
          text: 'The browser reads the text layer locally, without sending the file to a server.',
        },
        {
          name: 'Check the preview',
          text: 'If no text or very little text is found, the tool explains that the PDF may be a scan or image.',
        },
        {
          name: 'Download TXT',
          text: 'Download the extracted text as a plain .txt file.',
        },
      ],
    },
    'compare-pdf': {
      title: 'Compare PDFs',
      shortDescription:
        'Compare two PDFs by their text layer locally in your browser.',
      longDescription:
        'We compare two PDFs page by page using the existing text layer: matching, changed, added and removed pages are shown. Files are not uploaded to a server.',
      marketing: [
        'The tool compares the PDF text layer. It is fast and honest for contracts, invoices, specifications and other documents where the main question is what changed in the text.',
        'Files are read locally through pdf.js. We do not run visual pixel diff or OCR: those modes need separate work on quality, performance and render-noise handling.',
        'If a PDF is made of scans or images without a text layer, the tool explains the limitation immediately. For very large documents, split the file or compare shorter versions.',
      ],
      faq: [
        {
          q: 'How are the files compared?',
          a: 'Text-layer diff: the tool extracts the existing text layer from two PDFs and compares it page by page. Visual diff and OCR are outside this release.',
        },
        {
          q: 'Are files sent to a server?',
          a: 'No. Both PDFs are read directly in your browser through pdf.js. You can verify it in DevTools → Network: no requests carry your PDFs.',
        },
        {
          q: 'Does it work with scans?',
          a: 'No. If a PDF is built from page images and has no text layer, OCR is required. The tool shows a clear message in that case.',
        },
        {
          q: 'What does the result show?',
          a: 'A page summary, matching/changed/added/removed page counts, word deltas and text fragments that were removed or added.',
        },
        {
          q: 'Why is there no visual page comparison?',
          a: 'Visual diff needs page rendering, scale control and filtering for font/anti-aliasing noise. The tool uses a more stable text mode first.',
        },
      ],
      howTo: [
        {
          name: 'Upload the first PDF',
          text: 'Choose the original document version or drag it into the left upload area.',
        },
        {
          name: 'Upload the second PDF',
          text: 'Choose the newer document version in the second upload area.',
        },
        {
          name: 'Click "Compare PDFs"',
          text: 'The browser extracts the text layer from both files locally and compares pages.',
        },
        {
          name: 'Review the differences',
          text: 'Check the summary and the page fragments where text was added, removed or changed.',
        },
      ],
    },
    'extract-images-from-pdf': {
      title: 'Extract Images from PDF',
      shortDescription:
        'Extract embedded raster images from a PDF locally in your browser.',
      longDescription:
        'We inspect PDF image objects through pdf.js, show the found images and export them as PNG one by one or in a ZIP archive. The file is not uploaded to a server.',
      marketing: [
        'Use this when you need the actual embedded pictures from a PDF: illustrations, photos, scans inside a document or images from a presentation-style PDF.',
        'This is different from PDF to JPG: we do not render every page as a screenshot. We look for separate raster image objects inside the file. If the document is only text, vector artwork or already-flattened page scans, there may be no separate images to extract.',
        'Processing happens locally in the browser. The PDF is not uploaded; found images are saved as PNG, so original compression, EXIF and some complex masks may not be preserved.',
      ],
      faq: [
        {
          q: 'Is the file sent to a server?',
          a: 'No. The PDF is read in your browser through pdf.js. You can verify it in DevTools → Network: there will be no requests carrying your PDF.',
        },
        {
          q: 'How is this different from PDF to JPG?',
          a: 'PDF to JPG turns every page into an image. This tool tries to extract separate embedded images from the internal PDF structure.',
        },
        {
          q: 'Why might no images be found?',
          a: 'A PDF may not contain separate image objects: it can be built from text, vector shapes or full-page scans. Some masks, patterns and unusual image objects may also not be exposed by pdf.js as normal images.',
        },
        {
          q: 'What format do I download?',
          a: 'Found images are downloaded as PNG. When there are several images, a ZIP archive is available.',
        },
        {
          q: 'Are password-protected PDFs supported?',
          a: 'No. Remove password protection or encryption first, then extract images.',
        },
      ],
      howTo: [
        {
          name: 'Upload the PDF',
          text: 'Choose one PDF file or drag it into the upload area.',
        },
        {
          name: 'Click "Extract images"',
          text: 'The browser locally reads the PDF operator list and finds raster image objects.',
        },
        {
          name: 'Review the found images',
          text: 'The page shows previews, source page, pixel dimensions and PNG size for each result.',
        },
        {
          name: 'Download PNG or ZIP',
          text: 'Download a single image or the full set as one ZIP archive.',
        },
      ],
    },
    'flatten-pdf': {
      title: 'Flatten PDF',
      shortDescription:
        'Make filled PDF form fields non-editable locally in your browser.',
      longDescription:
        'Flatten PDF locks AcroForm fields: visible field values are drawn onto the pages and the fields are no longer editable. The file is not uploaded to a server.',
      marketing: [
        'Use it for filled PDF applications, forms and contracts when you need to send a copy without editable fields.',
        'Fields are locked locally via pdf-lib. Generic PDF comments, highlights, stamps, freehand annotations and XFA-only forms are not promised as supported in this version.',
      ],
      faq: [
        {
          q: 'Is the file sent to a server?',
          a: 'No. The PDF is read and saved in your browser via pdf-lib. There are no network requests carrying your file.',
        },
        {
          q: 'What exactly is flattened?',
          a: 'The tool supports AcroForm fields: text fields, checkboxes, radio buttons and similar standard form fields. Their visible values are drawn onto the pages and the fields become non-editable.',
        },
        {
          q: 'Are comments, highlights and stamps flattened?',
          a: 'Not guaranteed. pdf-lib reliably covers form fields, while arbitrary PDF annotations and freehand markups are outside this tool scope.',
        },
        {
          q: 'Are PDF pages rasterized into images?',
          a: 'No. Rendering every page to an image would hurt text search and quality. This tool keeps the PDF structure and locks only the supported form fields.',
        },
        {
          q: 'Are password-protected PDFs supported?',
          a: 'No. Remove password protection or encryption first, then flatten the form.',
        },
      ],
      howTo: [
        {
          name: 'Upload the PDF',
          text: 'Choose one PDF with filled form fields or drag it into the upload area.',
        },
        {
          name: 'Check the limits',
          text: 'The tool works with AcroForm fields. Comments, highlights, stamps and XFA-only forms may stay unchanged.',
        },
        {
          name: 'Click "Flatten PDF"',
          text: 'The browser locally draws visible field values onto the pages and removes editable form fields.',
        },
        {
          name: 'Download the result',
          text: 'Get the new PDF without uploading the source file to a server.',
        },
      ],
    },
    'fill-pdf-form': {
      title: 'Fill a PDF form',
      shortDescription: 'Fill existing interactive PDF fields locally in your browser.',
      longDescription:
        'Open an AcroForm document, fill text, checkboxes, radio buttons and lists, then download an editable or flattened copy. The file is not sent to a server.',
      marketing: [
        'Use this tool for applications and questionnaires where the PDF author already created interactive fields. The document and every entered value stay in your browser.',
        'This is not an arbitrary PDF editor: it does not place fields over ordinary text, OCR scans, support XFA or provide electronic signatures.',
      ],
      faq: [
        {
          q: 'Are the PDF and entered data sent to a server?',
          a: 'No. Parsing, filling, appearance updates and saving happen locally in a separate browser thread.',
        },
        {
          q: 'Which fields are supported?',
          a: 'Text fields, checkboxes, radio groups, dropdowns and option lists in a standard AcroForm.',
        },
        {
          q: 'Why can’t I fill a scan or ordinary PDF?',
          a: 'Those files have no interactive fields. OCR and arbitrary text placement are outside this tool.',
        },
        {
          q: 'What does flattening do?',
          a: 'It draws each visible field value onto the page and removes the ability to edit the fields later.',
        },
      ],
      howTo: [
        { name: 'Upload a PDF', text: 'Choose a PDF with existing interactive AcroForm fields.' },
        { name: 'Review the fields', text: 'The tool shows a bounded list of detected fields and their types.' },
        { name: 'Fill the form', text: 'Enter text and choose the required checkboxes, radio buttons and list options.' },
        { name: 'Download a copy', text: 'Optionally flatten the fields and download the new PDF.' },
      ],
    },
    'pdf-metadata': {
      title: 'PDF Metadata',
      shortDescription:
        'View and edit PDF title, author, subject, keywords and dates locally.',
      longDescription:
        'Read standard PDF metadata in the browser, edit or clear supported fields, and download a new PDF without sending the file to a server.',
      marketing: [
        'Use this when a PDF needs cleanup before archiving, publishing or sending: remove an old author, fix the title, subject, keywords or document dates.',
        'Processing runs locally through pdf-lib. The supported standard Info fields are Title, Author, Subject, Keywords, CreationDate and ModDate.',
        'Custom Info fields and the PDF XMP packet are handled honestly as unsupported for editing. We show that they exist and do not promise XMP synchronisation with form changes.',
      ],
      faq: [
        {
          q: 'Is the file sent to a server?',
          a: 'No. The PDF is read and saved in your browser via pdf-lib. There are no network requests carrying your file.',
        },
        {
          q: 'Which fields can I edit?',
          a: 'Title, Author, Subject, Keywords, CreationDate and ModificationDate. You can fill, change or clear them.',
        },
        {
          q: 'What happens to XMP and custom fields?',
          a: 'We show that they exist, but do not edit them. Those values remain in the PDF as-is; full XMP editing needs a separate specialised tool.',
        },
        {
          q: 'Can I remove metadata?',
          a: 'Yes, for supported fields: clear the value and save the PDF. Unsupported fields are not removed by this form.',
        },
        {
          q: 'Are password-protected PDFs supported?',
          a: 'No. Remove password protection or encryption first, then edit metadata.',
        },
      ],
      howTo: [
        {
          name: 'Upload the PDF',
          text: 'Choose one PDF file or drag it into the upload area.',
        },
        {
          name: 'Review detected fields',
          text: 'The page shows title, author, subject, keywords, dates and a separate notice about unsupported metadata.',
        },
        {
          name: 'Edit or clear values',
          text: 'Enter new values in supported fields. To remove a field, leave it empty.',
        },
        {
          name: 'Download the new PDF',
          text: 'The browser saves the PDF locally and returns a file with updated metadata.',
        },
      ],
    },
    'redact-pdf': {
      title: 'Redact PDF',
      shortDescription:
        'Cover PDF fragments and download a locally rebuilt image-only PDF.',
      longDescription:
        'Select areas on PDF pages. The browser rasterizes pages locally, paints over the selected fragments, and creates a new PDF without the original text layer.',
      marketing: [
        'We do not simply draw rectangles over the source PDF, because that can leave text inside the file. Instead, pages are turned into images, selected areas are painted at pixel level, and a new PDF is created.',
        'The limitation is explicit: the result is an image-only document. Text search, text selection, links, forms, comments, and some vector fidelity are not preserved. The original content stream is not copied into the new PDF.',
        'Processing runs locally through pdf.js and pdf-lib, with no server upload. This is not a legal or privacy guarantee for sensitive documents: check critical output in a separate PDF viewer before sharing it.',
      ],
      faq: [
        {
          q: 'Is this real redaction or only a visual overlay?',
          a: 'The tool rebuilds the PDF as image-only: pages are rendered to images, selected areas are painted over at pixel level, and the original text layer and content stream are not copied. That is safer than drawing a rectangle over existing text.',
        },
        {
          q: 'Is the file sent to a server?',
          a: 'No. The PDF is read, rendered, and rebuilt directly in your browser through pdf.js and pdf-lib. You can verify it in DevTools → Network: no requests carry your PDF.',
        },
        {
          q: 'Why do text selection and links disappear?',
          a: 'To avoid leaving hidden text behind the rectangles, the tool rasterizes pages. The new PDF consists of page images, so text, links, form fields, and comments are not preserved as editable objects.',
        },
        {
          q: 'Can I treat the result as legally guaranteed secure redaction?',
          a: 'No. The tool does not provide a legal or privacy guarantee. For critical documents, verify the output in a separate viewer and use a dedicated redaction workflow when formal control is required.',
        },
        {
          q: 'Are password-protected PDFs supported?',
          a: 'No. Remove password protection or encryption first, then redact the PDF.',
        },
      ],
      howTo: [
        {
          name: 'Upload the PDF',
          text: 'Choose one PDF file or drag it into the upload area.',
        },
        {
          name: 'Select redaction areas',
          text: 'Drag rectangles over the text or fragments you need to hide in the page preview.',
        },
        {
          name: 'Click "Redact and rebuild PDF"',
          text: 'The browser renders pages locally, paints the selected pixels, and creates a new image-only PDF.',
        },
        {
          name: 'Download and check the result',
          text: 'Open the new PDF in a separate viewer and make sure the intended areas are hidden.',
        },
      ],
    },
    unlockPdf: {
      title: 'Remove PDF Password',
      shortDescription:
        'Remove the opening password with the known password locally in your browser.',
      longDescription:
        'Open a protected PDF with the known password and save a copy that no longer asks for it. The file is processed locally and is not uploaded to a server.',
      marketing: [
        'If you know the PDF password but do not want to type it every time, save an unencrypted copy for regular work.',
        'This tool covers only the legitimate case: you enter the known password. We do not guess, recover or bypass unknown passwords.',
      ],
      faq: [
        {
          q: 'Is the file sent to a server?',
          a: 'No. The PDF is decrypted locally in your browser through QPDF/WASM. You can verify it in DevTools → Network: the file is not uploaded.',
        },
        {
          q: 'Can I remove a password if I do not know it?',
          a: 'No. pdfy does not crack PDFs, guess passwords or bypass protection. You need the known opening password for the document.',
        },
        {
          q: 'What happens with a wrong password?',
          a: 'The tool shows a clear error and does not create a new PDF. Try again with the correct password.',
        },
        {
          q: 'Which viewers should open the result?',
          a: 'The result is a regular unencrypted PDF. It should open in modern browsers, Adobe Acrobat, macOS Preview and other common PDF viewers.',
        },
        {
          q: 'Are there limitations?',
          a: 'A modern browser with WebAssembly threads and page isolation is required. Very old browsers or unusually encrypted PDFs may not process.',
        },
      ],
      howTo: [
        {
          name: 'Upload the PDF',
          text: 'Choose a protected PDF file or drag it into the upload area.',
        },
        {
          name: 'Enter the known password',
          text: 'Enter the document opening password. The tool does not work without a known password.',
        },
        {
          name: 'Click “Remove password”',
          text: 'The browser decrypts the PDF locally and saves a copy without encryption.',
        },
        {
          name: 'Download the result',
          text: 'Open the downloaded PDF in a regular viewer and check that it no longer asks for a password.',
        },
      ],
    },
    'repair-pdf': {
      title: 'Repair PDF',
      shortDescription:
        'Try to fix PDF structure locally through QPDF/WASM.',
      longDescription:
        'The tool rewrites the PDF through QPDF in the browser: it can help with some xref, trailer and incremental update damage, and it fails clearly when the content is lost or the file cannot be read.',
      marketing: [
        'Repair PDF is useful when a file opens only in some viewers, reports structural damage or was saved incorrectly after transfer.',
        'The tool does not upload the file to a server: QPDF runs locally through WebAssembly and creates a new PDF copy with rebuilt service tables.',
        'We do not promise impossible recovery: if the PDF was truncated during download, is encrypted without a password, contains damaged image streams or lost page objects, repair may be impossible.',
      ],
      faq: [
        {
          q: 'Is the file sent to a server?',
          a: 'No. Repair runs locally in the browser through QPDF/WASM. The file is not uploaded.',
        },
        {
          q: 'Which damage is supported?',
          a: 'The tool covers structural issues that QPDF can read and rewrite: some broken cross-reference tables or streams, trailer problems and extra/orphaned objects.',
        },
        {
          q: 'What cannot be repaired?',
          a: 'This tool does not fix truncated files, lost pages, damaged stream data, unknown passwords, malicious attachments or visual page quality.',
        },
        {
          q: 'Why can the repaired PDF have a different size?',
          a: 'QPDF rewrites the internal file structure and removes orphaned objects. The result may become larger or smaller without directly indicating repair quality.',
        },
        {
          q: 'What should I do if repair fails?',
          a: 'Try to get the source PDF again. For critical documents, try desktop PDF repair software.',
        },
      ],
      howTo: [
        {
          name: 'Upload the PDF',
          text: 'Choose the damaged PDF file or drag it into the upload area.',
        },
        {
          name: 'Start repair',
          text: 'The browser runs QPDF locally and tries to read the document with recovery enabled.',
        },
        {
          name: 'Download the new copy',
          text: 'If QPDF can rewrite the file, download the repaired PDF copy.',
        },
        {
          name: 'Check the result',
          text: 'Open the PDF in a regular viewer. If the error remains, the damage is deeper than the tool supports.',
        },
      ],
    },
    excelVPdf: {
      title: 'Excel to PDF',
      shortDescription:
        'Convert XLSX, XLS and ODS to PDF right in your browser: the file never leaves.',
      longDescription:
        'We convert Excel spreadsheets to PDF locally: each sheet becomes one or more landscape A4 pages. The file stays in your browser.',
      marketing: [
        'Convert an Excel spreadsheet to PDF for email, printing or archiving. XLSX, XLS and ODS are supported.',
        'Conversion runs right in your browser, so the file never leaves. Text and cell values are carried over. Colors, merged cells, charts and embedded images are not kept, and PDF pages are images, so their text cannot be selected. If you need an exact copy of the layout, save the PDF from Excel itself.',
      ],
      faq: [
        {
          q: 'Which input formats are accepted?',
          a: 'XLSX, XLS and ODS (OpenDocument Spreadsheet). This covers all Excel versions and LibreOffice Calc.',
        },
        {
          q: 'Is the file sent to a server?',
          a: 'No. The spreadsheet is read and turned into a PDF right in your browser. You can verify it in DevTools → Network.',
        },
        {
          q: 'Is the formatting preserved?',
          a: 'No. Cell values and sheet names are carried over. Colors, fonts, merged cells and charts are not kept. The first row of each sheet becomes a header on every page.',
        },
        {
          q: 'What is the maximum file size?',
          a: 'Up to 50 MB and up to 50,000 rows across the workbook. Processing runs on your device, so large spreadsheets take longer.',
        },
        {
          q: 'Are formulas preserved?',
          a: 'The PDF gets the formula values stored in the file. Live references and macros are not carried over — a PDF is static.',
        },
        {
          q: 'Are multiple sheets preserved?',
          a: 'Yes. Each sheet gets its own PDF pages with the sheet name on top.',
        },
        {
          q: 'Will wide tables fit?',
          a: 'All columns are fitted to the page width, so long values in wide tables are cut with an ellipsis. With more than 12 columns the tool warns you about it.',
        },
        {
          q: 'Are password-protected files supported?',
          a: 'No. Remove the password before converting.',
        },
        {
          q: 'Do I need to sign up?',
          a: 'No, the tool is completely free with no sign-up and no watermarks.',
        },
      ],
      howTo: [
        {
          name: 'Choose an Excel file',
          text: 'Pick an XLSX, XLS or ODS file with the button or drop it into the browser.',
        },
        {
          name: 'Press "Convert to PDF"',
          text: 'The browser reads the spreadsheet and builds the PDF on your device.',
        },
        {
          name: 'Wait for the result',
          text: 'A small spreadsheet takes seconds, a large one takes longer depending on your device.',
        },
        {
          name: 'Download the PDF',
          text: 'Download the finished PDF in one click and check that key columns are readable.',
        },
      ],
    },
    numeraciyaStranic: {
      title: 'Number PDF Pages',
      shortDescription: 'Add page numbers to your PDF: choose position, start number, and start page.',
      longDescription:
        'Add page numbers to your PDF — bottom center, bottom right, or top right. Set a custom start number and choose which page to start numbering from. All processing is done locally, files never leave your browser.',
      marketing: [
        'Add page numbers to your PDF document. Choose the number position: bottom center, bottom right, or top right.',
        'Configure the start number and the page to begin numbering from. Fully local processing — your file stays on your device.',
      ],
      faq: [
        {
          q: 'Is the file sent to a server?',
          a: 'No. Page numbering runs right in the browser using the pdf-lib library. Your file is not stored or transmitted anywhere.',
        },
        {
          q: 'What number positions are available?',
          a: 'Bottom center, bottom right, or top right. Switch between them anytime before processing.',
        },
        {
          q: 'Can I start numbering from a specific page?',
          a: 'Yes. Set a start number and choose which physical page to start numbering from. For example, start on page 5 with number 1.',
        },
        {
          q: 'What is the maximum file size?',
          a: 'Up to 100 MB.',
        },
        {
          q: 'Is registration required?',
          a: 'No, the tool is completely free and requires no registration.',
        },
        {
          q: 'Does the PDF content change?',
          a: 'Only page numbers are added — text, images and layout remain unchanged.',
        },
        {
          q: 'Are password-protected PDFs supported?',
          a: 'No. Please remove the password protection before numbering.',
        },
      ],
      howTo: [
        {
          name: 'Upload your PDF',
          text: 'Click the upload area or drag and drop a PDF file.',
        },
        {
          name: 'Choose the number position',
          text: 'Select where to display the number: bottom center, bottom right, or top right.',
        },
        {
          name: 'Configure numbering',
          text: 'Set the start number and the page to begin numbering from.',
        },
        {
          name: 'Click "Add numbers"',
          text: 'Processing takes seconds — right in your browser.',
        },
        {
          name: 'Download the result',
          text: 'Your PDF is ready for download with page numbers in place.',
        },
      ],
    },
    'watermark-pdf': {
      title: 'Watermark PDF',
      shortDescription: 'Add your own text watermark to a PDF locally in your browser.',
      longDescription:
        'Add a text watermark to every page of your PDF. Configure the text, opacity, position, and angle. The file stays in your browser and is not uploaded to a server.',
      marketing: [
        'Add your own text watermark to a PDF: draft, sample, confidential, or any other label you need.',
        'Settings apply to every page. Processing is fully local — the result is created in your browser without uploading the file.',
      ],
      faq: [
        {
          q: 'Is this a pdfy watermark?',
          a: 'No. The tool adds only the text you enter yourself. pdfy does not add logos, signatures, or restrictions to the output file.',
        },
        {
          q: 'Is the file sent to a server?',
          a: 'No. The watermark is added right in the browser via pdf-lib and canvas. The PDF is not uploaded or stored anywhere.',
        },
        {
          q: 'Can I use non-Latin text?',
          a: 'Yes. Text is first rendered by your browser as an image, so it supports the characters your browser can display.',
        },
        {
          q: 'Which pages get the watermark?',
          a: 'The watermark is applied to every page of the PDF.',
        },
        {
          q: 'Which settings are available?',
          a: 'Text, opacity, basic position, and angle. The diagonal option places the watermark in the center of the page.',
        },
        {
          q: 'Are password-protected PDFs supported?',
          a: 'No. Remove the password protection first, then add the watermark.',
        },
      ],
      howTo: [
        {
          name: 'Upload a PDF',
          text: 'Click the upload area or drag a PDF file into the browser.',
        },
        {
          name: 'Enter the text',
          text: 'Type the watermark text, for example "Draft", "Sample", or a company name.',
        },
        {
          name: 'Adjust the look',
          text: 'Choose opacity, position, and angle. Check the settings summary before processing.',
        },
        {
          name: 'Add the watermark',
          text: 'Click the processing button. The watermark is added to every page locally in your browser.',
        },
        {
          name: 'Download the result',
          text: 'Download the finished PDF with no sign-up and no extra service watermark.',
        },
      ],
    },
    'protect-pdf': {
      title: 'Protect PDF with Password',
      shortDescription: 'Add an open password to a PDF locally in your browser — no server upload.',
      longDescription:
        'Protect a PDF with an open password using QPDF in the browser. The password is required to view the result, but this is not DRM: we do not promise print, copy, or owner-side restrictions.',
      marketing: [
        'Add a password before sending a PDF by email, sharing it in a chat, or handing it to an external recipient. The output file asks for the password when opened.',
        'Processing runs locally in the browser through QPDF WASM. The source PDF and password are not sent to a server, and pdfy does not store or recover passwords.',
      ],
      faq: [
        {
          q: 'Is the file sent to a server?',
          a: 'No. Encryption runs locally in the browser through QPDF WASM. The source PDF and password are not uploaded.',
        },
        {
          q: 'Can you recover the password?',
          a: 'No. pdfy does not store the password and cannot restore access to the file. Save the password separately before sharing the document.',
        },
        {
          q: 'Does this prevent copying or printing?',
          a: 'No. This tool adds an open password. Print, copy, and edit restrictions are not reliable DRM protection, so we do not market them as security.',
        },
        {
          q: 'Which PDFs are supported?',
          a: 'Regular unencrypted PDFs up to 100 MB. If a file is already password-protected or damaged, remove protection or export a fresh PDF first.',
        },
        {
          q: 'Where will the protected PDF open?',
          a: 'The result is produced by QPDF with AES-256 encryption and is intended for modern PDF viewers: Acrobat, Chrome/Edge Preview, Preview.app, and system viewers.',
        },
      ],
      howTo: [
        {
          name: 'Upload a PDF',
          text: 'Choose one PDF file or drag it into the upload area.',
        },
        {
          name: 'Enter a password',
          text: 'Type the open password and repeat it in the second field to avoid typos.',
        },
        {
          name: 'Click "Protect PDF"',
          text: 'The PDF is encrypted locally in your browser. The file and password are not sent to a server.',
        },
        {
          name: 'Download the result',
          text: 'Download the protected PDF and check password opening in the viewer you need.',
        },
      ],
    },
    deletePdfPages: {
      title: 'Delete PDF Pages',
      shortDescription: 'Remove unwanted pages from a PDF by numbers or ranges right in your browser.',
      longDescription:
        'Delete selected pages from a PDF locally, with no server upload. Enter one page, several numbers, or ranges, and see how many pages will remain.',
      marketing: [
        'Remove unwanted PDF pages: blank sheets, duplicates, drafts, or attachments you do not need. You can enter single pages and ranges in one field.',
        'Processing runs locally via pdf-lib. The file stays in your browser, and the result downloads as a new PDF without the selected pages.',
      ],
      faq: [
        {
          q: 'Is the file sent to a server?',
          a: 'No. Page deletion runs right in your browser. The PDF is not uploaded or stored by us.',
        },
        {
          q: 'How do I enter pages to delete?',
          a: 'Enter comma-separated numbers: for example, 2,5-7 removes page 2 and pages 5 through 7.',
        },
        {
          q: 'Can I delete every page?',
          a: 'No. A PDF must keep at least one page, so the tool shows an error before processing that range.',
        },
        {
          q: 'Are out-of-document pages validated?',
          a: 'Yes. After upload, the tool counts PDF pages and rejects numbers greater than the document length.',
        },
        {
          q: 'What is the maximum file size?',
          a: 'Up to 100 MB. For very large documents, browser memory can still matter.',
        },
        {
          q: 'Are password-protected PDFs supported?',
          a: 'No. Remove the password or protection before deleting pages.',
        },
      ],
      howTo: [
        {
          name: 'Upload the PDF',
          text: 'Click the upload area or drag one PDF file into the browser.',
        },
        {
          name: 'Enter pages',
          text: 'Type the pages to remove: one page, a comma-separated list, or ranges like 2,5-7.',
        },
        {
          name: 'Check remaining pages',
          text: 'The tool shows how many pages will be removed and how many will remain.',
        },
        {
          name: 'Download the new PDF',
          text: 'Click "Delete pages" and download the finished file without the selected pages.',
        },
      ],
    },
    'reorder-pdf-pages': {
      title: 'Reorder PDF Pages',
      shortDescription: 'Put PDF pages in the order you need right in your browser.',
      longDescription:
        'Organize pages in one PDF locally: upload the file, drag pages into the right order, and download a new PDF without sending the source file to a server.',
      marketing: [
        'Fix the sequence of a document: move the cover page, attachments, scans, or signed pages to the position where they belong.',
        'Processing runs locally via pdf-lib. The source PDF stays in your browser, and the output is created as a new file with the same pages in the selected order.',
      ],
      faq: [
        {
          q: 'Is the file sent to a server?',
          a: 'No. The PDF is read and rebuilt right in your browser. You can verify it in the Network tab: the source file is not uploaded.',
        },
        {
          q: 'How do I reorder pages?',
          a: 'After uploading a PDF, drag page tiles with the mouse or use the up and down buttons on each tile.',
        },
        {
          q: 'Will I see page thumbnails?',
          a: 'The tool shows stable page tiles with original page numbers. That is faster and more reliable for large PDFs than rendering every page as an image.',
        },
        {
          q: 'What happens to PDF quality?',
          a: 'Pages are copied into a new PDF without re-rendering, so images and text are not recompressed.',
        },
        {
          q: 'What is the maximum file size?',
          a: 'Up to 100 MB. Large documents show rebuild progress, but speed still depends on browser memory and device performance.',
        },
        {
          q: 'Are password-protected PDFs supported?',
          a: 'No. Remove the password or protection before reordering pages.',
        },
      ],
      howTo: [
        {
          name: 'Upload the PDF',
          text: 'Choose one PDF file or drag it into the upload area.',
        },
        {
          name: 'Arrange pages',
          text: 'Drag page tiles into place or use the up and down buttons.',
        },
        {
          name: 'Check the order',
          text: 'Tiles show the original page number and the current position in the new document.',
        },
        {
          name: 'Download the new PDF',
          text: 'Click "Create PDF" and download the file with the selected page order.',
        },
      ],
    },
    'n-up-pdf': {
      title: 'Multiple PDF pages per sheet',
      shortDescription: 'Place 2, 4, or 8 PDF pages on an A4 or A3 sheet and download a print-ready file.',
      longDescription:
        'Prepare a PDF for compact printing: choose pages per sheet, paper size and orientation, margins, gaps, and fill order.',
      marketing: [
        'The preview composes the first output sheets from document pages and shows the actual paper dimensions before export.',
        'Pages are embedded into the new PDF as vector PDF objects with pdf-lib instead of being converted to images. The file stays in your browser.',
      ],
      faq: [
        {
          q: 'Is the PDF sent to a server?',
          a: 'No. Reading, preview, and export all happen locally in your browser.',
        },
        {
          q: 'Is text and graphics quality preserved?',
          a: 'Yes, where the source PDF allows it. Pages are embedded as vector objects without rasterisation, so text and lines stay sharp.',
        },
        {
          q: 'Which layouts are available?',
          a: '2, 4, or 8 source pages on one A4 or A3 sheet in portrait or landscape orientation.',
        },
        {
          q: 'Does this create duplex booklet signatures?',
          a: 'No. The tool fills sheets across rows or down columns but does not impose booklet signatures.',
        },
        {
          q: 'What limits apply?',
          a: 'Up to 100 MB and 400 source pages. These limits protect the browser tab from running out of memory.',
        },
      ],
      howTo: [
        { name: 'Add a PDF', text: 'Choose one PDF file; it stays in your browser.' },
        { name: 'Configure the sheet', text: 'Choose 2, 4, or 8 pages, A4 or A3, orientation, margins, and gaps.' },
        { name: 'Check the layout', text: 'Review the first output sheets, dimensions, and fill order.' },
        { name: 'Download the PDF', text: 'Build the vector PDF and download the print-ready file.' },
      ],
    },
    povernut: {
      title: 'Rotate PDF',
      shortDescription: 'Rotate PDF pages by 90°, 180° or 270° right in your browser.',
      longDescription:
        'Rotate all pages or a selected range. The file never leaves your browser — processing runs locally. Useful for scans in the wrong orientation.',
      marketing: [
        'Fix page orientation in a PDF: rotate by 90°, 180° or 270°. Apply to all pages or just the ones you need.',
        'Processing happens locally in your browser — your file is never uploaded. The result downloads instantly.',
      ],
      faq: [
        {
          q: 'Is the file sent to a server?',
          a: 'No. Rotation runs right in the browser via the pdf-lib library. Your file is not stored or transmitted anywhere.',
        },
        {
          q: 'How many degrees can I rotate by?',
          a: 'By 90°, 180° or 270° clockwise. For a 90° counter-clockwise rotation, pick 270°.',
        },
        {
          q: 'Can I rotate only specific pages?',
          a: 'Yes. Pick "Selected pages" and enter numbers or ranges like 1,3,5-7. You can also rotate every page at once.',
        },
        {
          q: 'What is the maximum file size?',
          a: 'Up to 50 MB.',
        },
        {
          q: 'Is registration required?',
          a: 'No, the tool is free and does not require sign-up.',
        },
        {
          q: 'Are password-protected PDFs supported?',
          a: 'No. Remove the protection before rotating.',
        },
        {
          q: 'Is quality preserved after rotation?',
          a: 'Yes. Rotation is PDF metadata, not a re-render. Images and text are not re-encoded, so quality stays identical to the original.',
        },
      ],
      howTo: [
        {
          name: 'Upload the PDF',
          text: 'Click the upload area or drag a PDF file in.',
        },
        {
          name: 'Pick a rotation angle',
          text: 'Choose 90°, 180° or 270°.',
        },
        {
          name: 'Pick the pages',
          text: 'Rotate every page or only selected ones (enter numbers in the 1,3,5-7 format).',
        },
        {
          name: 'Click "Rotate PDF"',
          text: 'Processing happens instantly, right in your browser.',
        },
        {
          name: 'Download the result',
          text: 'The finished PDF downloads in one click.',
        },
      ],
    },

    mergePdf: {
      title: 'Merge PDF',
      shortDescription: 'Combine multiple PDF files into one right in your browser.',
      longDescription:
        'Merge several PDF files into a single document. Reorder files before merging. Files stay in your browser — no upload to server.',
      marketing: [
        'Combine several PDF files — contracts, scans, reports — into one document. Drag files to reorder before merging.',
        'Merging happens locally in your browser — files never leave your device. The result downloads instantly.',
      ],
      faq: [
        {
          q: 'Are files sent to a server?',
          a: 'No. Merging happens right in your browser using the pdf-lib library. Your files are never stored or transmitted.',
        },
        {
          q: 'How many files can I merge?',
          a: 'Each file can be up to 100 MB. The number of files is not limited, but consider browser memory for very large documents.',
        },
        {
          q: 'Can I reorder the files?',
          a: 'Yes. After uploading, use the up/down arrow buttons to change file order before merging.',
        },
        {
          q: 'Is page quality preserved?',
          a: 'Yes. pdf-lib copies pages without recompression, keeping the original quality of all PDF elements.',
        },
        {
          q: 'Do I need to sign up?',
          a: 'No. The tool is completely free and requires no registration.',
        },
        {
          q: 'Can I merge password-protected PDFs?',
          a: 'No. Password-protected PDFs cannot be processed without removing the password first.',
        },
      ],
      howTo: [
        {
          name: 'Select files',
          text: 'Click the upload area or drag PDF files into the browser. You can add multiple files.',
        },
        {
          name: 'Adjust the order',
          text: 'Reorder files using the up/down arrows, remove unwanted files, or add more files.',
        },
        {
          name: 'Merge and download',
          text: 'Click "Merge PDF" and download the combined document.',
        },
      ],
    },

    splitPdf: {
      title: 'Split PDF',
      shortDescription: 'Extract selected pages from a PDF into a new file right in your browser.',
      longDescription:
        'Save selected PDF pages as a separate document. Enter a range like 1-3, 7, 10-12 — the file is not uploaded to a server.',
      marketing: [
        'Select the pages you need from a contract, scan or report and save only that part as a new PDF. Page order follows the range you enter.',
        'Splitting happens locally in your browser via pdf-lib. The file is not uploaded, stored or watermarked.',
      ],
      faq: [
        {
          q: 'Is the file sent to a server?',
          a: 'No. PDF splitting runs right in your browser. Your file is not uploaded to a server and is not stored by us.',
        },
        {
          q: 'How do I select pages?',
          a: 'Enter page numbers and ranges separated by commas: 1-3, 7, 10-12. You can specify pages in the order you need.',
        },
        {
          q: 'Can I download every page as a separate file?',
          a: 'The tool saves selected pages into one new PDF.',
        },
        {
          q: 'What is the maximum file size?',
          a: 'Up to 100 MB. Very large documents may still hit browser memory limits.',
        },
        {
          q: 'Do I need to sign up?',
          a: 'No. The tool is free, with no sign-up and no watermarks.',
        },
        {
          q: 'Are password-protected PDFs supported?',
          a: 'No. Remove password protection first, then extract pages.',
        },
      ],
      howTo: [
        {
          name: 'Upload the PDF',
          text: 'Click the upload area or drag a PDF file into the browser.',
        },
        {
          name: 'Enter pages',
          text: 'Type a page range, for example 1-3, 7, 10-12. The tool checks syntax and document bounds immediately.',
        },
        {
          name: 'Click "Extract pages"',
          text: 'Selected pages are copied into a new PDF locally, with no file upload.',
        },
        {
          name: 'Download the result',
          text: 'The finished PDF with selected pages is available in one click.',
        },
      ],
    },

    'crop-pdf': {
      title: 'Crop PDF',
      shortDescription: 'Crop PDF margins by exact values or a visual crop frame in your browser.',
      longDescription:
        'Adjusts the CropBox on every PDF page locally, with no server upload. Enter exact margins in millimetres or drag the preview frame.',
      marketing: [
        'Remove extra white margins, scanner edges, or utility borders before sharing, printing, or publishing a PDF. The same crop is applied to every page.',
        'Cropping runs locally via pdf-lib: content is not re-compressed, the file is never uploaded, and the result downloads as a new PDF.',
      ],
      faq: [
        {
          q: 'Is the file sent to a server?',
          a: 'No. The PDF is read and cropped right in your browser. There are no network requests carrying your file.',
        },
        {
          q: 'What changes inside the PDF?',
          a: 'The tool sets page CropBox values. This is the standard PDF visible-area box: image and text quality is unchanged because pages are not rendered again.',
        },
        {
          q: 'Can I crop different pages differently?',
          a: 'In this version, the same margins are applied to every page. That keeps the tool simple for scanned documents and files with consistent margins.',
        },
        {
          q: 'What if pages have different sizes?',
          a: 'After upload, the tool shows the first page size and the smallest page size. If the chosen margins do not fit the smallest page, cropping is blocked.',
        },
        {
          q: 'What is the maximum file size?',
          a: 'Up to 100 MB. Very large PDFs can still be limited by browser memory.',
        },
        {
          q: 'Are password-protected PDFs supported?',
          a: 'No. Remove the password or protection first, then crop the document.',
        },
      ],
      howTo: [
        {
          name: 'Upload a PDF',
          text: 'Choose one PDF file or drag it into the upload area.',
        },
        {
          name: 'Set the crop area',
          text: 'Enter top, right, bottom, and left margins in millimetres or drag the preview frame edges.',
        },
        {
          name: 'Check dimensions',
          text: 'The tool shows the first page and smallest page dimensions so the crop fits the whole document.',
        },
        {
          name: 'Download the result',
          text: 'Click "Crop PDF" — the new file is created locally in your browser with no server upload.',
        },
      ],
    },

  },

  documentScanner: {
    pickGallery: 'Choose document photos', drop: 'Drop JPG, PNG or WebP files here, or click to choose',
    limits: 'Up to {pages} pages, {size} MB total, {megapixels} MP per photo, up to {outputMegapixels} MP in the PDF', camera: 'Take a page photo', localNotice: 'Photos stay in your browser.',
    pageCount: 'Pages: {count}', pageTitle: 'Page {page}', previewAlt: 'Preview of page {page}', moveUp: 'Move page {page} up', moveDown: 'Move page {page} down', remove: 'Remove', rotate: 'Rotate 90°',
    modes: { color: 'Color', grayscale: 'Grayscale', 'black-white': 'B&W' }, brightness: 'Brightness', contrast: 'Contrast', crop: 'Crop visible page edges, %', cropSides: { top: 'Top', right: 'Right', bottom: 'Bottom', left: 'Left' },
    disclaimer: 'No OCR or automatic perspective correction. The result is a cleaned PDF, not a certified or official scan.', export: 'Build scanned PDF', processing: 'Processing pages…', download: 'Download PDF',
    errors: { format: 'Only JPG, PNG and WebP files are supported.', page_count: 'You can add no more than 20 pages.', total_size: 'The photos are larger than 50 MB in total.', dimensions: 'A photo is larger than 36 megapixels. Resize it before upload.', corrupt: 'Could not read the photo. The file may be corrupted.', output_limit: 'The pages exceed the 64 MP output limit. Resize photos or remove pages.', processing: 'Could not build the PDF. Try other photos or an up-to-date browser.' },
  },

  szhat: {
    targetLegend: 'Target size',
    targetNone: 'No target size',
    targetNoneHint: 'Simply reduce the PDF at the selected quality',
    targetFiveMb: 'Up to 5 MB',
    targetFiveMbHint: 'Best effort: check whether the result fits',
    targetBestEffort:
      '5 MB is a target, not a guarantee. The result depends on the PDF structure and selected quality.',
    levelLegend: 'Compression level',
    levelStrong: 'Strong',
    levelStrongHint: 'Smaller file — more quality loss',
    levelStandard: 'Standard',
    levelStandardHint: 'Balance of size and quality',
    levelMin: 'Minimal',
    levelMinHint: 'Light compression — closer to the original',
    submit: 'Compress PDF',
    loadingLocal: 'Compressing locally…',
    again: 'Compress another',
    resultTitle: 'Compression complete',
    sizeBefore: 'Before',
    sizeAfter: 'After',
    savings: 'Saved',
    noopTitle: 'File is already compact',
    noopBody: 'This PDF has no images that can be further compressed without quality loss. Download as-is or pick another file.',
    noopDownload: 'Download as-is',
    classifying: 'Analysing PDF…',
    classifyError: 'The PDF could not be read. The file may be damaged: try another one or repair this one.',
    encryptedError: 'The PDF is password-protected. Unlock it before compressing.',
    wasmError: 'The PDF could not be compressed in this browser. Try another compression level or update your browser.',
    retryLocal: 'Try again',
    chooseAnotherFile: 'Choose another file',
    targetLimit: 'Limit',
    targetHitTitle: 'The file fits the limit',
    targetHitBody: 'The output PDF is no larger than {target}. You can download it and upload it where that limit is required.',
    targetMissTitle: 'The limit was not reached yet',
    targetMissBody:
      'The output PDF is still larger than {target}. Try the strongest available mode; if that is not enough, reduce source images, remove extra pages or export the document again with lower image quality.',
    targetNoopMissBody:
      'The file is larger than {target}, but it has no images that can be honestly compressed without visible quality loss. Reduce the source file or remove extra pages before trying again.',
    targetTryStronger: 'Try strong compression',
    weakSavingsHint: 'The file barely shrank: it has few images that can be recompressed. Remove extra pages or export the document again with lower image quality.',
  },

  vJpg: {
    h1: 'PDF to JPG online',
    intro:
      'Convert PDF pages to JPG images. Each page becomes a separate file; multi-page documents are bundled into a ZIP. The file is processed right in your browser and never sent to a server.',
    dpiLegend: 'Quality (DPI)',
    dpi72Hint: 'For web and social — faster, smaller',
    dpi150Hint: 'Versatile quality for most tasks',
    dpi300Hint: 'Print quality — larger files',
    openingPdf: 'Opening PDF…',
    pageOf: 'Page {current} of {total}',
    largeWarn: 'Large document — this may take a while. Do not close the tab.',
    submit: 'Convert to JPG',
    loading: 'Converting…',
    again: 'Convert another',
    downloadImage: 'Download JPG',
    downloadZip: 'Download ZIP',
    previewAlt: 'Preview of the first page',
    pagesLabel: 'Pages',
    encrypted: 'The PDF is password-protected — remove the password and try again.',
    canvasUnavailable: 'Your browser does not support canvas — please update and try again.',
    oom:
      'The browser ran out of memory while rendering. Try a lower quality (72 or 150 DPI) or a smaller PDF.',
    parseError: 'Could not read the PDF. The file may be corrupted.',
  },

  pdfToPng: {
    h1: 'PDF to PNG online',
    intro:
      'Convert PDF pages to PNG images. Each page becomes a separate file; multi-page documents are bundled into a ZIP. The file is processed right in your browser and never sent to a server.',
    dpiLegend: 'Resolution (DPI)',
    dpi72Hint: 'For quick previews and web pages',
    dpi150Hint: 'Clear, versatile output',
    dpi300Hint: 'High resolution — larger files',
    openingPdf: 'Opening PDF…',
    pageOf: 'Page {current} of {total}',
    largeWarn: 'Large document — this may take a while. Do not close the tab.',
    submit: 'Convert to PNG',
    loading: 'Converting…',
    again: 'Convert another',
    downloadImage: 'Download PNG',
    downloadZip: 'Download ZIP',
    previewAlt: 'Preview of the first PNG page',
    pagesLabel: 'Pages',
    encrypted: 'The PDF is password-protected — remove the password and try again.',
    canvasUnavailable: 'Your browser does not support canvas — please update and try again.',
    oom:
      'The browser ran out of memory while rendering. Try a lower resolution (72 or 150 DPI) or a smaller PDF.',
    parseError: 'Could not read the PDF. The file may be corrupted.',
  },

  excelVPdf: {
    submit: 'Convert to PDF',
    classifying: 'Inspecting spreadsheet…',
    loadingLocal: 'Converting locally…',
    again: 'Convert another',
    resultTitle: 'Conversion complete',
    downloadPdf: 'Download PDF',
    pdfLabel: 'PDF',
    dropHint: 'The file stays on your device: conversion runs in the browser',
    convertError: 'The spreadsheet could not be read. The file may be damaged or password-protected.',
    tooManyRows: 'The workbook has more than 50,000 rows, which is too much for a browser. Split the table into parts.',
    wideNotice: 'The table is wide, so long values may be cut. Check the PDF or remove extra columns.',
  },

  numeraciyaStranic: {
    h1: 'Number PDF Pages Online',
    intro:
      'Add page numbers to your PDF document. Choose the number position, start number, and start page. Processing is done right in your browser — files never leave your device.',
    positionLegend: 'Number position',
    positionBottomCenter: 'Bottom center',
    positionBottomRight: 'Bottom right',
    positionTopRight: 'Top right',
    startNumberLabel: 'Start number',
    startPageLabel: 'Start from page',
    submit: 'Add numbers',
    loading: 'Adding numbers…',
    again: 'Number another',
    totalPages: 'Pages',
    encrypted: 'PDF is password-protected — please remove the password and try again.',
    parseError: 'Could not read the PDF. The file may be damaged.',
    startPageTooHigh: 'This PDF has only {pages} pages. Choose a start page no higher than that.',
  },

  watermarkPdf: {
    h1: 'Add a Watermark to PDF Online',
    intro:
      'Add your own text watermark to every page of a PDF. Configure text, opacity, position, and angle. Everything runs locally in your browser — the file is not uploaded.',
    defaultText: 'Sample',
    textLabel: 'Watermark text',
    textPlaceholder: 'For example: Draft',
    opacityLabel: 'Opacity',
    positionLegend: 'Position',
    positionCenter: 'Center',
    positionTopLeft: 'Top left',
    positionBottomRight: 'Bottom right',
    positionDiagonal: 'Diagonal',
    angleLegend: 'Angle',
    summaryTitle: 'Check settings before downloading',
    summaryText: 'Text',
    summaryOpacity: 'Opacity',
    summaryPosition: 'Position',
    summaryAngle: 'Angle',
    textEmpty: 'Not set',
    submit: 'Add watermark',
    loading: 'Adding watermark…',
    again: 'Add another',
    totalPages: 'Pages',
    emptyText: 'Enter watermark text.',
    encrypted: 'PDF is password-protected — please remove the password and try again.',
    parseError: 'Could not process the PDF. The file may be damaged.',
  },

  protectPdf: {
    passwordLabel: 'Open password',
    repeatPasswordLabel: 'Repeat password',
    passwordWarning:
      'pdfy does not store the password and cannot recover the file. Save the password separately before sharing the protected PDF.',
    submit: 'Protect PDF',
    loading: 'Encrypting PDF…',
    downloadProtected: 'Download protected PDF',
    again: 'Protect another PDF',
    passwordTooShort: 'Password must be at least 4 characters long.',
    passwordMismatch: 'Passwords do not match. Check both fields.',
    encryptedInput: 'This PDF is already password-protected or encrypted. Remove protection first.',
    encryptError: 'Could not protect this PDF. The file may be damaged or unsupported by QPDF.',
  },

  deletePdfPages: {
    h1: 'Delete PDF Pages Online',
    intro:
      'Upload a PDF, enter unwanted pages, and download a new document without them. Processing is local — the file is never sent to a server.',
    pagesLabel: 'Pages to delete',
    rangesPlaceholder: 'For example: 2,5-7',
    rangesHintPrefix: 'Comma-separated pages and ranges, for example ',
    rangesHintSuffix: '.',
    pageCount: 'pages: {pages}',
    totalPages: 'Total pages',
    removedPages: 'Will be removed',
    remainingPages: 'Pages remaining',
    submit: 'Delete pages',
    loading: 'Deleting pages…',
    reading: 'Counting PDF pages…',
    again: 'Delete from another PDF',
    invalidRanges: 'Could not parse pages. Example: 2,5-7',
    outOfRange: 'This PDF has only {pages} pages. Enter page numbers inside the document.',
    reversedRange: 'The range must go upward: for example, 5-7, not 7-5.',
    emptyResult: 'You cannot delete every page — the PDF must keep at least one page.',
    encrypted: 'The PDF is password-protected — remove the password and try again.',
    parseError: 'Could not read the PDF. The file may be damaged.',
  },

  reorderPdfPages: {
    h1: 'Reorder PDF Pages Online',
    intro:
      'Upload a PDF, arrange pages in the order you need, and download a new file. Everything runs locally — the source PDF is never sent to a server.',
    pageCount: 'pages: {pages}',
    pagesTitle: 'Page order',
    currentPosition: 'Position {page}',
    originalPage: 'original {page}',
    dragHandle: 'Drag page',
    moveUp: 'Move page {page} up',
    moveDown: 'Move page {page} down',
    resetOrder: 'Reset order',
    changedSummary: 'Changed positions: {count}',
    noChanges: 'The order still matches the source PDF.',
    largePdf:
      'This document has {pages} pages. We use stable tiles without rendering thumbnails and show rebuild progress so the browser does not sit without feedback.',
    progressTitle: 'Creating the new PDF',
    progressPages: 'Page {current} of {total}',
    totalPages: 'Total pages',
    movedPages: 'Pages moved',
    submit: 'Create PDF',
    loading: 'Creating PDF…',
    reading: 'Counting PDF pages…',
    again: 'Reorder another PDF',
    singlePage: 'This PDF has one page, so there is nothing to reorder.',
    encrypted: 'The PDF is password-protected — remove the password and try again.',
    parseError: 'Could not process the PDF. The file may be damaged.',
  },

  nUpPdf: {
    sourcePages: 'Source PDF pages: {count}',
    replaceFile: 'Replace file',
    pagesPerSheet: 'Pages per sheet',
    pagesPreset: '{count} per sheet',
    paperSize: 'Paper size',
    orientation: 'Orientation',
    portrait: 'Portrait',
    landscape: 'Landscape',
    margin: 'Margins',
    gap: 'Gap',
    pageOrder: 'Page order',
    across: 'Across rows',
    down: 'Down columns',
    dimensions: 'Actual sheet size: {width} × {height} mm',
    pageLimit: 'up to {count} pages',
    previewTitle: 'Output sheet preview',
    previewError: 'Could not display page previews, but export is still available.',
    previewLimited: 'Showing the first {shown} of {total} sheets.',
    outputSheets: 'Output sheets: {count}',
    sheetLabel: 'Sheet {count}',
    submit: 'Build print-ready PDF',
    loading: 'Building PDF…',
    resultSummary: 'Sheets: {sheets} · file size: {size}',
    again: 'Another PDF',
    errors: {
      tooLarge: 'The PDF exceeds the 100 MB limit.',
      tooManyPages: 'Up to {count} source pages are supported.',
      encrypted: 'The PDF is password-protected. Remove protection and try again.',
      emptyDocument: 'The PDF has no pages. Choose a document with at least one page.',
      invalidLayout: 'The margins and gaps do not fit on the selected sheet.',
      parseError: 'Could not process the PDF. The file may be damaged.',
    },
  },

  mergePdf: {
    h1: 'Merge PDF files online',
    intro:
      'Combine multiple PDF files into one document. Reorder files before merging. Everything is processed in your browser — no server upload.',
    pickLabel: 'Select PDF files',
    dragPdfs: 'Drag PDFs here or ',
    filesLabel: 'Files to merge',
    minFilesHint: 'Add at least 2 PDF files to merge',
    moveUp: 'Move up',
    moveDown: 'Move down',
    clearAll: 'Clear all',
    submit: 'Merge PDF',
    loading: 'Merging…',
    again: 'Merge another',
    mergedFiles: 'Files merged',
    totalPages: 'Total pages',
    noPdf: 'Please add at least one PDF file.',
    encrypted: 'One of the files is password-protected — remove the password before merging.',
    parseError: 'Could not process one of the files. It may be corrupted.',
  },

  splitPdf: {
    h1: 'Split PDF online',
    intro:
      'Extract selected pages from a PDF into a new file. Enter a page range — everything is processed locally in your browser and never sent to a server.',
    pickLabel: 'Select a PDF file',
    rangesLabel: 'Pages to extract',
    rangesPlaceholder: 'For example: 1-3, 7, 10-12',
    rangesAria: 'Page ranges to extract',
    rangesHintPrefix: 'Comma-separated pages and ranges, for example ',
    rangesHintSuffix: '.',
    totalPages: '{pages} pages',
    totalPagesLabel: 'Total pages',
    selectedPages: 'Selected pages',
    emptyRanges: 'Enter at least one page.',
    invalidRanges: 'Could not parse the range. Example: 1-3, 7, 10-12',
    outOfRange: 'This PDF has only {pages} pages. Choose pages inside the document.',
    reversedRange: 'Ranges must go upward: 3-5, not 5-3.',
    inspecting: 'Reading PDF…',
    submit: 'Extract pages',
    loading: 'Extracting…',
    again: 'Split another',
    encrypted: 'The PDF is password-protected — remove the password and try again.',
    parseError: 'Could not read the PDF. The file may be damaged.',
  },

  cropPdf: {
    h1: 'Crop PDF online',
    intro:
      'Upload a PDF, set margins or drag the preview crop frame, and download the cropped file. Everything runs locally in your browser.',
    pageCount: 'pages: {pages}',
    previewLabel: 'Visual crop frame',
    previewHint:
      'Drag the top, right, bottom, or left frame edge. You can fine-tune the same values in millimetres below.',
    marginsLabel: 'Crop margins, mm',
    marginTop: 'Top',
    marginRight: 'Right',
    marginBottom: 'Bottom',
    marginLeft: 'Left',
    marginsHint: 'Margins are applied to every PDF page.',
    firstPageSize: 'First page size',
    minPageSize: 'Smallest page',
    dimensionValue: '{width} × {height} mm',
    pagesProcessed: 'Pages cropped',
    firstPageCrop: 'First page visible area',
    reading: 'Reading PDF dimensions…',
    submit: 'Crop PDF',
    loading: 'Cropping…',
    again: 'Crop another PDF',
    emptyCrop: 'Set at least one margin or drag the crop frame.',
    invalidMargins: 'Margins must be zero or positive numbers.',
    tooLargeCrop:
      'These margins leave an empty or too small page. Reduce the values.',
    encrypted: 'The PDF is password-protected — remove the password and try again.',
    parseError: 'Could not read the PDF. The file may be damaged.',
    dragTop: 'Drag the top crop edge',
    dragRight: 'Drag the right crop edge',
    dragBottom: 'Drag the bottom crop edge',
    dragLeft: 'Drag the left crop edge',
  },

  redactPdf: {
    pageCount: 'pages: {pages}',
    boxCount: 'areas: {count}',
    limitsTitle: 'Not a visual overlay on the PDF',
    limitsBody:
      'The result is rebuilt as an image-only PDF: pages are rendered to images, selected areas are painted at pixel level, and the original text layer and links are not carried over. This is an honest approach without a legal redaction guarantee.',
    reading: 'Preparing page previews…',
    editorTitle: 'Select redaction areas',
    editorHint:
      'Drag a rectangle with a mouse or finger over the text. Click an existing rectangle to remove it.',
    pageLabel: 'Page {page}',
    pageBoxCount: 'areas: {count}',
    previewAria: 'Preview of page {page}',
    removeBox: 'Remove redaction area',
    noBoxesHint: 'Add at least one area in the preview before rebuilding the PDF.',
    noBoxes: 'Add at least one redaction area.',
    openingPdf: 'Opening PDF…',
    pageOf: 'Processing page {current} of {total}',
    submit: 'Redact and rebuild PDF',
    loading: 'Rebuilding…',
    clear: 'Clear',
    again: 'Redact another PDF',
    pagesProcessed: 'Pages processed',
    boxesApplied: 'Areas applied',
    resultNote:
      'The new PDF consists of page images. Text under the selected areas is not copied, but text search, links, and forms are not preserved in the result.',
    encrypted: 'The PDF is password-protected. Remove protection and try again.',
    renderError:
      'Could not render the pages in this browser. Try a smaller PDF or close extra tabs.',
    parseError: 'Could not read the PDF. The file may be damaged.',
  },

  povernut: {
    h1: 'Rotate PDF online',
    intro:
      'Rotate PDF pages by 90°, 180° or 270°. All pages or a selected range. The file is processed right in your browser and never sent to a server.',
    angleLegend: 'Rotation angle',
    pagesLegend: 'Which pages',
    pagesAll: 'All pages',
    pagesRanges: 'Selected',
    rangesPlaceholder: 'For example: 1,3,5-7',
    rangesAria: 'Page ranges',
    rangesHintPrefix: 'Comma-separated pages and ranges, for example ',
    rangesHintSuffix: '.',
    invalidRanges: 'Could not parse page ranges. Example: 1,3,5-7',
    submit: 'Rotate PDF',
    loading: 'Rotating…',
    again: 'Rotate another',
    rotatedCount: 'Pages rotated',
    encrypted: 'The PDF is password-protected — remove the password before rotating.',
    parseError: 'Could not process the PDF. The file may be corrupted.',
  },

  blog: {
    breadcrumb: 'Blog',
    indexH1: 'pdfy blog',
    indexIntro:
      'Guides and notes about working with PDFs, local file processing and pdfy tools.',
    empty: 'No posts in this language yet. Switch the language to see what is available.',
    updated: 'Updated',
    faqHeading: 'FAQ',
    relatedToolsLabel: 'Related tools',
    relatedToolsHeading: 'Related tools',
    backToBlog: '← All blog posts',
    notFoundTitle: 'Post not found',
    notFoundBody: 'The link may be outdated or the URL was entered incorrectly.',
    notFoundDoc: 'Post not found — pdfy.ru',
    otherLanguage: 'This post is written in {language}.',
    viewTranslation: 'Open the {language} translation',
  },

  seo: {
    index: {
      title: 'All PDF tools online and free — pdfy.ru',
      description:
        'Compress, convert, and rotate PDFs online for free. No sign-up or watermarks; works in your browser on any device.',
    },
    pdfKonverter: {
      title: 'Free online PDF converter — pdfy.ru',
      description:
        'Convert Excel, HTML, images, and PDFs right in your browser. Free PDF tools with no sign-up or watermarks.',
    },
    blog: {
      title: 'pdfy blog — PDF guides and tutorials',
      description:
        'Guides and practical notes about PDFs: compression, conversion, rotation, and local browser processing.',
    },
  },
}
